/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * OrtbBodyEditor (#858) — authors a demand source's free-form OpenRTB request
 * body. The OpenRTB analog of the VAST request-params editor.
 *
 * Two synchronized views over one {@link OrtbBodyTemplate}:
 *  - **Guided** — a collapsible tree of body leaves; each leaf's mapping is set
 *    via {@link MappingControl}. Paste a sample BidRequest to auto-map fields
 *    (via the shared #859 suggest utility).
 *  - **Raw** — the serialized JSON. Read-only in this pass; upgrades to a
 *    CodeMirror editor with schema completion (spec §4.7).
 *
 * Shell-independent content component — no nav/routing assumptions. Chakra +
 * live semantic tokens, dark-mode aware. Emits the exact {@link OrtbBodyTemplate}
 * the driver (#862) resolves.
 */

import { getValueSuggestionsForSurface } from '../inputs/data-model-schema';
import { issueLevel, rowSeverity, worstIssue, type MappingIssue } from './mapping-issues';
import type { FloorBound } from '../../../utils/floor-bound';
import { NumberField } from '../inputs/NumberField';
import type { FieldImportance } from '@destination-field';
import { omissionOf, omissionToRow } from '@wire-omission';
import React from 'react';
import { useExpressionNameLookup } from '../../../hooks/useExpressionLibrary';
import { Badge, Box, Button, HStack, Input, NativeSelect, Text, Textarea, VStack } from '@chakra-ui/react';
import { MappingControl } from './MappingControl';
import { MappingRow, RowStateMark, type TemplateRevert } from './MappingRow';
import { rowsAfterApply, rowsAfterCancel, type RowEditSnapshot } from './row-edit-snapshot';
import { suggestsSecretValue } from '@sensitive-headers';
import { useSystemDefaults } from '../../../hooks/useConfig';
import type { RowMappingKind } from './parameter-editor.adapters';
import type { MacroExpression } from '../../../types/decorator-expression';
import { LoadingState } from '../../ui/states';

/**
 * **The raw editor is loaded when Raw JSON is opened** (`#1331`). It is built on CodeMirror, and the
 * guided view is the common path, so a static import made every console page carry CodeMirror in the
 * main bundle for a view most visits never open.
 */
const OrtbRawJsonEditor = React.lazy(() =>
  import('./OrtbRawJsonEditor').then((module) => ({ default: module.OrtbRawJsonEditor })),
);
import { allCatalogNames } from '@field-catalog';
import { useRevealOnOpen } from './useRevealOnOpen';
import { rowAnchorId, useFindingReview, useRowOpenRequest, type RowOpenRequest } from './row-open-request';
import { diagnosePastedSample, type PastedSampleDiagnosis } from './pasted-sample';
import {
  getFieldSuggestions,
  getMissingStandardFields,
  getMoveOffers,
  ORTB_STANDARD_SECTIONS,
} from '@ortb-field-catalog';
import { macroExpression, ORTB_ROOT_SECTION, pathWithinSection, sectionOfLeaf } from './ortb-body-editor.adapters';
import {
  catalogSegments,
  ORTB_PATTERN_SECTIONS,
  patternScopeOf,
  patternScopeRank,
  type PatternScope,
} from './ortb-body-editor.adapters';
import { validateOrtbBody, type OrtbFieldRef } from './ortb-body-validation';
import { MappingAutocompleteInput, type MappingSuggestion } from '../inputs/MappingAutocompleteInput';
import { FieldHelpTooltip } from '../../ui/FieldHelpTooltip';
import { diagnoseTypographicQuotes } from './json-normalize';
import { IssueDot, ValidationSummary } from './ValidationSummary';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import type { WireRendering } from '@wire-rendering';
import { formatStaticValue, isEmptyStaticValue } from './ortb-static-value';
import {
  type MappingLeaf,
  bodyToLeaves,
  buildStarterLeaves,
  catalogValueKindForPath,
  bodyLeafRendering,
  autoSentGroups,
  classifyPastedBody,
  revertDetectedLeaf,
  overriddenMergeBaseField,
  bodyRemovalReplacement,
  catalogConceptMembersForPath,
  conceptResolutionForLeaf,
  conceptWireLabelForPath,
  describeLeafRequiredMark,
  expandStructuredLeaves,
  defaultLeafFor as sharedDefaultLeafFor,
  filterLeafGroups,
  groupLeavesBySection,
  initiallyCollapsedSections,
  groupHoldsRef,
  readRememberedCollapsed,
  writeRememberedCollapsed,
  leavesToBody,
  leavesToTemplate,
  leavesWithFixes,
  leafName,
  renameLeaf,
  pathToLabel,
  templateToLeaves,
} from './ortb-body-editor.adapters';
import type { OrtbBodyTemplate } from '../../../types/demand-source';
import { ProvenanceLegend } from './ProvenanceLegend';
import { resolutionForLeaf } from './resolved-request';
import type { RequestPreview } from '../../../hooks/useRequestPreview';
import type { ArtifactInheritance } from '../../../types';
import { InheritanceMark } from '../InheritanceMark';
import {
  draftRowInheritance,
  rowInheritanceProvenance,
  rowLayerActionKind,
  isWholeSectionLeaf,
  ortbBodyLeafPaths,
  ortbBodySectionPath,
  bodyRemovalPath,
} from '../../../utils/artifact-inheritance';
import { ParentRemovalRows, useStopOverriding, type ParentRemovals } from './parent-removals';
import { originOf } from './parameter-editor.adapters';
import {
  replaceValueInLeaves,
  rowOccurrenceMarks,
  surfaceConflictIssues,
  withSurface,
  type RequestOccurrences,
} from './request-occurrences';
import { useApplyToAll, type LocalValueChange } from './useApplyToAll';
import { layerActionFor, originMarks, type RowOriginAnswer } from './row-origin-marks';
import { useDraftRevert } from './useDraftRevert';
import { describeExpressionPill, leafAddressesChangedExpression, leafDefault } from './mapping-value-round-trip';
import { summarizeMacroExpression, type ExpressionNameLookup } from './expression-summary';
import type { SavedLibraryEntry } from './DecoratorExpressionEditor';

export interface OrtbBodyEditorProps {
  /**
   * **Where this viewer's open and shut groups are kept for the session** (`#1453`, Todd
   * 2026-09-28: *"per person for the session"*) — the source's id on the detail page, shared by its
   * read and edit views so opening the editor keeps the layout. `null` where there is no stable
   * identity to keep it under; the tree then opens by rule on every visit. Required and nullable
   * (`#1134`), so a surface that does not remember is saying so.
   */
  rememberKey: string | null;
  /**
   * **What the source's terms do to an authored floor** (`#918`) — `floorBoundOf` over the source
   * being edited. Required and nullable (`#1134`): a surface that does not know the terms passes
   * `null`, and a `bidfloor` below the source's minimum then goes unremarked there.
   */
  floorBound: FloorBound | null;
  value?: OrtbBodyTemplate | null;
  onChange: (template: OrtbBodyTemplate) => void;
  /**
   * Which leaves come from a parent (`P3a`) — threaded rather than resolved here, so this surface
   * and the parameter/header editors read the same answer. Nullable, not optional, for the reason
   * `preview` is: an optional prop through a shared component renders nothing and type-checks.
   *
   * **Answered per leaf outside an array and per section inside one** (#1143). `layerFor` replaces
   * an array wholesale, so `imp` is a single leaf in the layer and nothing beneath it is
   * addressable; the section says so once rather than every row implying a granularity that does
   * not exist. See `ortbBodyLeafPaths`.
   */
  inheritance?: ArtifactInheritance | null;
  /**
   * **Where else this request sends each row's value** (`#944`) — the index the page builds over
   * parameters, headers and body together. Required and nullable: `null` is a surface with no request
   * to count against, and forgetting it would compile and render nothing.
   */
  requestOccurrences: RequestOccurrences | null;
  /** The leaves **as stored**, when this editor is showing a draft over them. */
  baselineLeaves?: MappingLeaf[];
  /**
   * **The template this source was copied from, for the way back to it** (Todd, 2026-09-24). A row
   * the operator changed from a template reads `overridden here`, and its revert puts back what the
   * template has for that row **now**. The source stays a copy (`TM9a`); this reads the template at
   * the moment of the revert and tracks nothing afterwards.
   *
   * **Required and nullable** (`#1134`): `null` where the source came from no template, the viewer
   * cannot see it, or the surface is read-only.
   */
  fromTemplate: { name: string; leaves: readonly MappingLeaf[] } | null;
  /** Give these leaves back to the parent (`P3a`). Omit inside an open draft — see `HeadersEditor`. */
  onRevert?: (paths: string[]) => void | Promise<unknown>;
  /** The parent's name, when the viewer may know it. */
  parentName?: string;
  /** True while a revert is in flight. */
  isReverting?: boolean;
  /**
   * **The parent's body leaves this source removes, by label** (`#1379`, the body half of `#1126`) —
   * leaves that override a field the driver builds, which the operator stopped overriding so the
   * driver's own value goes out.
   *
   * Required and nullable, as on the parameter and header editors: `null` is a surface with no
   * parent (the wizard). The shape differs from theirs in one place, `onChange`, which hands back a
   * **template** rather than rows, because this editor's value is a template.
   */
  parentRemovals: {
    names: readonly string[];
    stored: readonly string[];
    onChange: ((next: { names: string[]; template: OrtbBodyTemplate }) => void) | null;
  } | null;
  /** Invoked when the operator runs a quick test (resolve against a sample session). */
  onQuickTest?: () => void;
  /**
   * The source's OpenRTB protocol version ('2.5' | '2.6'). Makes the field catalog
   * version-aware. Defaults to 2.6 (the catalog's authored baseline).
   *
   * **What that means changed with `N11h` (#1112).** It used to pick between two entries for one
   * concept — `video.plcmt` on 2.6, `video.placement` on 2.5 — which asked the operator to choose
   * a field name that is the receiver's business. There is one entry now, and the version decides
   * **which members of its vocabulary can be expressed**: 2.5 has a faithful value for `instream`
   * alone, so it is the only one offered there.
   *
   * **Required, `null` for *the default*** (`#1318` item 2). It decides what *Add recommended*
   * offers, what the starter seeds and what validation warns about, so a mount that forgot it
   * silently authored against another version's catalog.
   */
  ortbVersion: string | null;
  /**
   * **Is the artifact being edited a template?** — which decides what an unfilled *required* leaf
   * means on the row (`describeLeafRequiredMark`). On a template it is the shape (*You supply
   * this*); on an instance it is unfinished.
   *
   * **Required and nullable**, the `#1134` treatment: `null` is a surface saying *I do not know
   * which this is* — the wizard, until the save phase asks — and a leaf then states only that it
   * is required, which is true either way. Optional would have made this render nothing on
   * whichever surface forgot it, which is the defect that rule exists for.
   */
  isTemplate: boolean | null;
  /**
   * Promote a mapping into the shared expression library — **required, `null` to decline**.
   *
   * Absent here until 2026-08-27, which made this `#1134`'s sixth instance and the worst-placed:
   * a body leaf is where an operator is most likely to build a reusable expression, and the button
   * simply did not render. Found by making every prop on `MappingControl` required rather than by
   * anyone noticing — which is the point of doing that.
   */
  onSaveExpressionToLibrary:
    | ((name: string, description: string, expr: MacroExpression) => Promise<SavedLibraryEntry>)
    | null;

  /**
   * Read-only preview (detail-page): the guided tree with section expand/collapse
   * and leaf chips, but no toolbar/paste/add/edit chrome — only the body's rows.
   */
  readOnly?: boolean;
  /**
   * Resolved-request preview (R4a/R4e) — what each leaf will actually send.
   *
   * **Required, and nullable rather than optional** (2026-08-05) — see the note on
   * `HeadersEditorProps.preview`. `null` means "no source context to resolve
   * against" and must be written explicitly, so an unwired call site is a type
   * error rather than a silently blank column.
   */
  preview: RequestPreview | null;
  /**
   * **The row a finding asked this editor to open or reveal** (`#1305`), or `null` where nothing on
   * the surface can point at a row — a read-only preview, or a caller with no findings panel.
   *
   * Required and nullable, the treatment every prop on a shared control carries since `#1134`.
   */
  openRow: RowOpenRequest | null;
}

/**
 * **Recognition, which is a third question and not the matcher's** (#1190).
 *
 * This asks *is `{x}` a macro we resolve* — so it reads **every** catalog spelling, including
 * the ones `MACRO_VOCABULARY` declines. A decline says *never infer this from somebody else's
 * name*; it says nothing about the name's validity, and `{bundle}` and `{max_duration}` are
 * macros the resolver resolves. Wiring this to the inferable set instead would have made an
 * operator's correct macro read as unknown — the decline leaking out of the one decision it
 * is about.
 *
 * It read the console's hand-kept `MACRO_DEFAULTS`, which is why `{bundle}` warns today.
 */
const KNOWN_MACROS = new Set(allCatalogNames().map((k) => k.toLowerCase()));

type View = 'guided' | 'raw';

/**
 * One-line summary of a leaf's mapping — the row's pill (#989).
 *
 * **Returns the same shape as `describeRowMapping`**, which is the point. The two
 * described one concept and disagreed about it: for a static leaf this said the *word*
 * `static` and put the literal in a column of its own, while the list editors' pill **is**
 * the literal. So the same information sat in two different places depending on which
 * section of one page you were looking at, and the body needed a third column the list
 * editors do without.
 *
 * With the literal in the pill the static column has nothing left to show and is gone
 * (Todd, 2026-08-07), and the row converges on `MappingRow` completely.
 *
 * The palette vocabulary converges too — `RowMappingKind` rather than a private
 * `info | accent | neutral | warning`, so `MappingRow`'s `CHIP` map decides the colours
 * for every surface instead of each surface picking its own.
 */
function leafSummary(leaf: MappingLeaf, nameFor: ExpressionNameLookup): { label: string; kind: RowMappingKind } {
  if (leaf.expression?.type === 'macro') {
    const token = leaf.expression.name;
    // An unknown macro keeps its warning tint — it resolves to nothing on the wire, which
    // is the one thing about a pill worth colouring for.
    return { label: `{${token}}`, kind: KNOWN_MACROS.has(token.toLowerCase()) ? 'macro' : 'empty' };
  }
  if (leaf.kind === 'expression') {
    // The list editors' rule, not a copy of it: a path shows the path, and a shared expression says
    // `shared: <name>` rather than its type word (`efacee1a` named it on parameters and headers and
    // this surface was left out).
    return leaf.expression
      ? describeExpressionPill(leaf.expression, (e) => summarizeMacroExpression(e, nameFor))
      : { label: 'expression', kind: 'expression' };
  }
  // An empty static (no value, or an empty list/object) needs attention — it would send as
  // an empty value. `0`/`false` are valid and not flagged. `not set` rather than `empty`,
  // matching the list editors' word for the same state.
  if (isEmptyStaticValue(leaf.staticValue)) return { label: 'not set', kind: 'empty' };
  return { label: formatStaticValue(leaf.staticValue), kind: 'static' };
}

const IMPORTANCE_LABEL: Record<FieldImportance, string> = { required: 'req', recommended: 'rec', optional: 'opt' };

export const OrtbBodyEditor: React.FC<OrtbBodyEditorProps> = ({
  rememberKey,
  value,
  onChange,
  onQuickTest,
  ortbVersion,
  isTemplate,
  onSaveExpressionToLibrary,
  readOnly = false,
  fromTemplate,
  preview,
  inheritance = null,
  requestOccurrences,
  baselineLeaves,
  onRevert,
  parentName,
  isReverting,
  parentRemovals: parentRemovalsProp,
  openRow,
  floorBound,
}) => {
  // The library once, above the leaves — see `useExpressionNameLookup`. It names a shared
  // expression a leaf addresses and carries the default in force for the row's `⤵` mark.
  const expressionNameFor = useExpressionNameLookup();
  const [leaves, setLeaves] = React.useState<MappingLeaf[]>(() => templateToLeaves(value ?? undefined));
  const occurrences = React.useMemo(
    () => (requestOccurrences ? withSurface(requestOccurrences, 'ortbBody', { ortbBody: leaves }) : null),
    [requestOccurrences, leaves],
  );
  const applyToAll = useApplyToAll(occurrences);
  const conflictIssues = React.useMemo(() => surfaceConflictIssues(occurrences, 'ortbBody'), [occurrences]);

  /**
   * Has this leaf diverged from what is stored? Keyed by its **label**, which is the leaf's own
   * address in this editor and survives a re-projection; nothing else identifies a leaf.
   *
   * `provenance` is excluded because it is authoring metadata rather than the value — a leaf whose
   * chip changed has not changed what it sends.
   */
  const storedByLabel = React.useMemo(
    () => new Map((baselineLeaves ?? []).map((leaf) => [pathToLabel(leaf.path), leaf])),
    [baselineLeaves],
  );
  const isLeafChanged = React.useCallback(
    (leaf: MappingLeaf) => {
      const stored = storedByLabel.get(pathToLabel(leaf.path));
      if (!stored) return false;
      const strip = ({ provenance: _p, ...rest }: MappingLeaf) => rest;
      return JSON.stringify(strip(stored)) !== JSON.stringify(strip(leaf));
    },
    [storedByLabel],
  );

  const [mode, setMode] = React.useState<'merge' | 'replace'>(value?.mode ?? 'merge');

  /**
   * A leaf's inheritance chip and its revert, or its authoring provenance — the same one-or-other
   * rule the parameter and header rows follow. `ortbBodyLeafPaths` returns nothing for a leaf inside
   * an array, so those rows fall through to their authoring provenance and the **section** carries
   * the inheritance statement instead (#1143).
   */
  /**
   * **The removals, over this editor's leaves** (`#1379`) — the shape the shared *stop overriding*
   * code takes. The page keeps a template, so a change is handed up as one; the leaves are this
   * editor's own state and move with it, exactly as `emit` moves them.
   */
  const parentRemovals: ParentRemovals<MappingLeaf> | null = React.useMemo(() => {
    const outer = parentRemovalsProp ?? null;
    if (!outer) return null;
    const handUp = outer.onChange;
    return {
      names: outer.names,
      stored: outer.stored,
      onChange: handUp
        ? ({ names, rows }) => {
            setLeaves(rows);
            handUp({ names, template: leavesToTemplate(rows, mode) });
          }
        : null,
    };
  }, [parentRemovalsProp, mode]);

  /** Revert in a draft or out of one — see `useDraftRevert` and the parameter editor (`#1126`). */
  const revertFromParent = useDraftRevert({
    onRevert,
    baselineRows: baselineLeaves,
    rows: leaves,
    keyOf: (leaf: MappingLeaf) => pathToLabel(leaf.path),
    keysOf: (paths) => {
      const covers = (bases: readonly string[]) =>
        paths.some((p) => bases.some((base) => p === base || p.startsWith(`${base}.`)));
      const fromLeaves = [
        ...new Set([...leaves, ...(baselineLeaves ?? [])].map((leaf) => pathToLabel(leaf.path))),
      ].filter((label) => {
        const leaf = [...leaves, ...(baselineLeaves ?? [])].find((l) => pathToLabel(l.path) === label)!;
        return covers(ortbBodyLeafPaths(leaf.path));
      });
      // A removal has no leaf at all, so its label is asked about too — see the parameter editor.
      const fromRemovals = (parentRemovalsProp?.names ?? []).filter((label) => covers([bodyRemovalPath(label)]));
      return [...new Set([...fromLeaves, ...fromRemovals])];
    },
    // Called after the source reloads, never during render — by then `emit` below exists. A reverted
    // removal leaves the list with the parent's leaf back in the draft, in one change (`#1379`).
    apply: (nextLeaves, reverted) => {
      const removals = parentRemovals;
      if (removals?.onChange && removals.names.some((n) => reverted.has(n))) {
        removals.onChange({ names: removals.names.filter((n) => !reverted.has(n)), rows: nextLeaves });
      } else {
        emit(nextLeaves, mode);
      }
    },
  });

  /**
   * **Revert a draft-only change to a leaf** (`InheritanceMark.restoreDraft`) — the stored leaf, which
   * is the parent's, back into the draft. Through a ref because what it needs (`emit`, the open row)
   * is declared further down; it is assigned after render, and only ever called from a click.
   */
  const restoreLeafRef = React.useRef<(label: string, stored: MappingLeaf) => void>(() => undefined);

  const leafInheritanceMark = React.useCallback(
    (leaf: MappingLeaf): RowOriginAnswer => {
      const paths = ortbBodyLeafPaths(leaf.path);
      const state = draftRowInheritance(inheritance, paths, isLeafChanged(leaf));
      if (!state) {
        // **A leaf on a path the driver builds is an override, and the row now says so**
        // (Todd, 2026-08-27: *"we have the overridden badge, perhaps that's a better way to
        // show 'driver built' but changed by the author."*). The merge base is structurally a
        // parent — the value comes from somewhere else and this replaces it — so it reuses the
        // relation's own vocabulary rather than teaching a second one.
        //
        // The leaf's own provenance wins where it has one: *where this value came from* is more
        // specific than *what it replaces*, and two chips about one value is what a single
        // vocabulary exists to prevent.
        return {
          origin: leaf.provenance ?? (overriddenMergeBaseField(leaf, mode) ? originOf('overridden') : undefined),
        };
      }
      return {
        inheritance: (
          <InheritanceMark
            inheritance={inheritance}
            path={paths[0]}
            state={state}
            parentName={parentName}
            isReverting={isReverting}
            onRevert={revertFromParent ?? undefined}
            revertsNow={baselineLeaves !== undefined}
            restoreDraft={
              storedByLabel.has(pathToLabel(leaf.path))
                ? () => restoreLeafRef.current(pathToLabel(leaf.path), storedByLabel.get(pathToLabel(leaf.path))!)
                : undefined
            }
          />
        ),
      };
    },
    [inheritance, isLeafChanged, revertFromParent, baselineLeaves, parentName, isReverting, mode, storedByLabel],
  );
  const [view, setView] = React.useState<View>('guided');
  /**
   * **Leaving Raw JSON while it does not parse asks first** (`#1332`, ruled by Todd 2026-09-16). The
   * Guided view is built from the last text that parsed, so anything typed since is lost on the
   * switch. Asked rather than refused: an operator may want Guided to rebuild the JSON for them.
   */
  const [confirmLeaveRaw, setConfirmLeaveRaw] = React.useState(false);
  const chooseView = (next: View) => {
    if (next === 'guided' && view === 'raw' && rawError) {
      setConfirmLeaveRaw(true);
      return;
    }
    setView(next);
  };
  const effectiveView: View = readOnly ? 'guided' : view;
  /**
   * **Which groups are shut** — `null` until the tree has been drawn once, when `LP14`'s rule decides
   * it (below, beside the findings it reads). After that it is the operator's.
   */
  const [collapsedState, setCollapsedState] = React.useState<Set<string> | null>(null);
  // Assigned below, once the findings the rule reads exist; read only from handlers and effects.
  let firstDraw: Set<string> = new Set();
  const setCollapsed = (next: Set<string> | ((prev: Set<string>) => Set<string>)) =>
    setCollapsedState((prev) => (typeof next === 'function' ? next(prev ?? firstDraw) : next));
  const [filter, setFilter] = React.useState('');
  const [editing, setEditing] = React.useState<string | null>(null);
  /** The row as it was when its editor opened — restored by Cancel. */
  const [editSnapshot, setEditSnapshot] = React.useState<RowEditSnapshot<MappingLeaf> | null>(null);
  // Whether a value marked secret is stored encrypted on this deployment (`CR7`); `undefined` says nothing.
  const encryptedAtRest = useSystemDefaults().data?.runtimeSecretsEncrypted;
  const [pasteOpen, setPasteOpen] = React.useState(false);
  const [pasteText, setPasteText] = React.useState('');
  const [pasteError, setPasteError] = React.useState<string | null>(null);
  // Set when a parse failure is diagnosed as smart quotes and a normalized
  // re-parse would succeed — drives the one-click "Fix quotes" affordance.
  const [pasteQuoteFix, setPasteQuoteFix] = React.useState(false);
  /**
   * **What the paste decided, per field** (`#1187`). It was one string naming the *sections* it
   * had skipped, which told an operator neither which fields were taken nor why — and the paste
   * box is cleared on apply, so that sentence was the only account of it that would ever exist.
   */
  const [pasteNotes, setPasteNotes] = React.useState<string[]>([]);
  const [addingTo, setAddingTo] = React.useState<string | null>(null);
  const [newKey, setNewKey] = React.useState('');
  /**
   * **Which impressions a field being added to `imp` is for** (RS4c) — every impression unless the
   * operator picks one. Reset each time the add row opens, so a choice made for one field is never
   * silently carried to the next.
   */
  // `null` is *a numbered impression, with no valid number yet* — the add is refused until there is
  // one, rather than the control guessing a number for the operator (the `NumberField` rule).
  const [addScope, setAddScope] = React.useState<PatternScope | null>({ kind: 'each' });

  /**
   * Guidance §22 — the worst instance of the problem this exists for. A real bid body
   * runs to dozens of leaves across eight sections, so a field added to `imp` or `user`
   * lands well below the fold, and an operator who sees nothing move concludes the Add
   * did not take.
   */
  const revealRef = useRevealOnOpen<HTMLDivElement>(editing);

  const [rawError, setRawError] = React.useState<string | null>(null);
  // Normalized text when a raw parse failure is diagnosed as smart quotes and the fix
  // would work — drives the same one-click repair the paste dialog offers. Raw is where
  // a whole pasted BidRequest lands, so it needs the fix at least as much (#943).
  const [rawQuoteFix, setRawQuoteFix] = React.useState<string | null>(null);
  /**
   * A sample bid request appears to have been pasted into Raw JSON rather than into
   * Paste & parse (#922 · 5) — the two windows look alike and do opposite things, and
   * getting it wrong pins one viewer's IP, UA and device id into config permanently.
   *
   * Held as an offer, never applied: the operator may genuinely want a literal, and
   * silently macro-mapping their document would be the mirror-image defect.
   */
  const [pastedSample, setPastedSample] = React.useState<PastedSampleDiagnosis | null>(null);
  /**
   * Dismissed for the session. Without this the offer returns on the very next
   * keystroke — the literals are still there, so the diagnosis is still true — and a
   * prompt that cannot be dismissed is one the operator learns to click past.
   */
  const [sampleOfferDismissed, setSampleOfferDismissed] = React.useState(false);
  // Bumped on programmatic body changes (Fix, paste) to re-seed the uncontrolled
  // raw editor so they show there; NOT bumped on raw typing, so the cursor is safe.
  const [rawSeed, setRawSeed] = React.useState(0);
  const [pendingSections, setPendingSections] = React.useState<string[]>([]);
  const [customSection, setCustomSection] = React.useState('');
  const [addingCustomSection, setAddingCustomSection] = React.useState(false);

  /**
   * The single funnel for every model change in this editor — and therefore the one
   * place the resolution mark has to be told the model moved (R4e).
   *
   * It told it **nothing**, and only `applyEdit` ever refreshed. So every bulk action
   * — paste a sample, add recommended, add or remove a field, edit the raw JSON —
   * left ~20 rows each looking themselves up in the request resolved *before* the
   * change, finding nothing, and rendering `→ omitted`. Editing any single row then
   * Applied, which refreshed, which fixed all of them at once. That is the tell, and
   * it is #999 in a third place after the headers editor and the detail page.
   *
   * `commit` (the default) resolves; `{ live: true }` only invalidates, for typing
   * inside an open row where a resolve per keystroke would be a request per keystroke.
   * Note it is `preview.commit()` rather than `refresh()` — resolving in this tick
   * would resolve the model as it was *before* this call. See `useRequestPreview`.
   */
  const emit = React.useCallback(
    (nextLeaves: MappingLeaf[], nextMode: 'merge' | 'replace', opts?: { live?: boolean }) => {
      setLeaves(nextLeaves);
      setMode(nextMode);
      onChange(leavesToTemplate(nextLeaves, nextMode));
      if (opts?.live) preview?.invalidate();
      else preview?.commit();
    },
    [onChange, preview],
  );

  React.useLayoutEffect(() => {
    restoreLeafRef.current = (label, stored) => {
      if (editing === label) {
        setEditing(null);
        setEditSnapshot(null);
      }
      emit(
        leaves.map((l) => (pathToLabel(l.path) === label ? stored : l)),
        mode,
      );
    };
  });

  const updateLeaf = (label: string, next: MappingLeaf) => {
    // Live typing inside an open row — invalidate only. `applyEdit` is the commit.
    emit(
      leaves.map((l) => (pathToLabel(l.path) === label ? next : l)),
      mode,
      { live: true },
    );
  };

  /**
   * Close the open row, expanding any object typed into a static box into its
   * own fields — the same rows raw JSON would have produced. Deferred to close
   * rather than done per keystroke: expanding mid-edit would pull the row the
   * operator is typing in out from under them.
   */
  const applyExpansion = (base: MappingLeaf[] = leaves): MappingLeaf[] => {
    const expanded = expandStructuredLeaves(base);
    if (expanded.length === base.length) {
      // `base` differs from what is held only when Apply confirmed a guess on it (`#1001`).
      if (base !== leaves) emit(base, mode);
      return base;
    }
    emit(expanded, mode);
    setRawSeed((n) => n + 1); // reflect the new fields in the uncontrolled raw editor
    return expanded;
  };

  /** Apply: keep what was typed, accept a detected guess, expand any object into its own fields, close. */
  const applyEdit = () => {
    const label = editing;
    const held = editSnapshot;
    const current = leaves.find((l) => pathToLabel(l.path) === label);
    // See `RequestParameterEditor` — a shared value asks first, and the answer is one commit (`#944`).
    const commit = (change: LocalValueChange | null) => {
      setEditing(null);
      setEditSnapshot(null);
      // Open and Apply accepts the leaf (`#1001`) — `rowsAfterApply`, beside Cancel's rule. Confirmed
      // before expanding, in one emit, because `applyExpansion` reads the leaves it is handed.
      let next = rowsAfterApply(leaves, (l) => pathToLabel(l.path) === label, held);
      if (change) next = replaceValueInLeaves(next, change);
      applyExpansion(next);
      if (change) occurrences?.applyElsewhere?.apply({ ...change, except: 'ortbBody' });
      // Apply is the commit boundary (§13) and therefore the resolve trigger (R4e).
      preview?.refresh();
    };
    if (!current || !label) return commit(null);
    applyToAll.apply({
      before: held && held.origin === 'existing' ? held.row : null,
      after: current,
      self: { surface: 'ortbBody', name: label },
      commit,
    });
  };

  /**
   * Cancel: put the row back to what it was when the editor opened — or take the field away, where
   * the gesture that opened the editor is what added it (`#1262`).
   *
   * **This editor used to keep it**, on the argument that *removing the field is the ✕ action, not
   * an edit-discard*. Ruled against by Todd 2026-09-11: adding and opening are one click, so Cancel
   * undoes one act, and the default mapping the counter-case wanted to preserve is one Apply away.
   * The rule is stated once, in `row-edit-snapshot.ts`, for all five editors.
   */
  const cancelEdit = () => {
    const snapshot = editSnapshot;
    const label = editing;
    setEditing(null);
    setEditSnapshot(null);
    if (!snapshot || !label) return;
    emit(
      rowsAfterCancel(leaves, snapshot, (l) => pathToLabel(l.path) === label),
      mode,
    );
  };

  const openEditor = (label: string) => {
    const current = applyExpansion();
    const row = current.find((l) => pathToLabel(l.path) === label);
    setEditSnapshot(row ? { row, origin: 'existing' } : null);
    setEditing(label);
  };

  /**
   * **How this editor answers _which row is that_** — a leaf is addressed by its dotted path, which
   * is what `validateOrtbBody` puts in a finding's `path` and `pathToLabel` renders as the row's
   * own key. The two spellings agree by construction: `leafDisplayPath` and `pathToLabel` are the
   * same reduction, and this is the one editor where the finding's vocabulary and the editor's key
   * are already one string.
   *
   * **And it expands the section first.** A leaf inside a collapsed group renders nothing at all,
   * so an anchor to reveal and a row to open would both be absent — the operator would land on the
   * editor and see the section they were pointed at still shut. A finding names a **section** when
   * it is structural (*no `imp`*, *no inventory object*); those resolve to no leaf, which is honest
   * — there is no row to open.
   */
  /** A section this body has leaves in — what a structural finding names instead of a leaf. */
  const holdsSection = (ref: string): boolean => leaves.some((l) => sectionOfLeaf(l) === ref.trim());
  const holdsLeaf = (ref: string): boolean => leaves.some((l) => pathToLabel(l.path) === ref.trim());
  const resolveLeafRow = (ref: string): string | null => {
    const wanted = ref.trim();
    const leaf = leaves.find((l) => pathToLabel(l.path) === wanted);
    if (!leaf) {
      // **A section is a place Review can go**, not a dead end: the reveal scrolls to the header's
      // anchor, which carries the same id shape. Expanded, so the operator lands on its contents.
      if (!holdsSection(wanted)) return null;
      setCollapsed((prev) => {
        if (!prev.has(wanted)) return prev;
        const next = new Set(prev);
        next.delete(wanted);
        return next;
      });
      return wanted;
    }
    const section = sectionOfLeaf(leaf);
    setCollapsed((prev) => {
      if (!prev.has(section)) return prev;
      const next = new Set(prev);
      next.delete(section);
      return next;
    });
    return pathToLabel(leaf.path);
  };
  const openLeafRow = (label: string) => {
    // Only a leaf opens. A section has no editor to open, so an `open` naming one reveals it instead.
    if (holdsLeaf(label)) openEditor(label);
  };
  useRowOpenRequest<string>('ortbBody', openRow, resolveLeafRow, openLeafRow);
  // `resolveLeafRow` expands a section, so it cannot answer *do I hold that row* while rendering.
  const findingReview = useFindingReview(
    'ortbBody',
    resolveLeafRow,
    (ref) => holdsLeaf(ref) || holdsSection(ref),
    openLeafRow,
  );
  const removeLeaf = (label: string) => {
    emit(
      leaves.filter((l) => pathToLabel(l.path) !== label),
      mode,
    );
    if (editing === label) {
      setEditing(null);
      setEditSnapshot(null);
    }
  };

  /** *Stop overriding* and its Undo — shared with the parameter and header editors (`parent-removals.tsx`). */
  const { stopOverriding, undo: undoStopOverriding } = useStopOverriding<MappingLeaf>({
    parentRemovals,
    rows: leaves,
    nameOf: (leaf) => pathToLabel(leaf.path),
    isSameRow: (a, b) => pathToLabel(a.path) === pathToLabel(b.path),
    onRowLeaving: (leaf) => {
      if (editing === pathToLabel(leaf.path)) {
        setEditing(null);
        setEditSnapshot(null);
      }
    },
    commit: () => preview?.commit(),
  });

  const groups = groupLeavesBySection(leaves);
  const unresolvedCount = leaves.filter((l) => leafSummary(l, expressionNameFor).kind === 'empty').length;

  // Sections the user added that have no leaves yet render as empty groups.
  const realSections = new Set(groups.map((g) => g.section));
  const allGroups = [
    ...groups,
    ...pendingSections.filter((s) => !realSections.has(s)).map((s) => ({ section: s, leaves: [] as MappingLeaf[] })),
  ];
  const presentSections = new Set(allGroups.map((g) => g.section));
  const STANDARD_SECTIONS = ORTB_STANDARD_SECTIONS;

  /**
   * Leaf filter (#922 · 4). A real bid body carries dozens of leaves across eight
   * sections — the #916 sample has ~45 — so finding `imp.video.plcmt` meant expanding
   * sections and reading down them.
   *
   * Matched against the **full dotted label**, not the section-relative name, so
   * `imp.video` narrows to one section's video block and `plcmt` finds it wherever it
   * lives. That is the same address the catalog, the validator and the resolution mark
   * all use — one path vocabulary on every surface, #1000's rule.
   *
   * A filtered section **expands regardless of its collapse state**, and does not
   * disturb it: a search that hides its own matches inside a collapsed section is worse
   * than no search, and one that permanently expands what the operator had collapsed
   * loses their arrangement the moment they clear the box.
   */
  const filtering = filter.trim() !== '';
  const visibleGroups = filterLeafGroups(allGroups, filter);
  const filterMatchCount = filtering ? visibleGroups.reduce((n, g) => n + g.leaves.length, 0) : 0;

  const toggleSection = (section: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const bodyIssues = validateOrtbBody(
    leavesToTemplate(leaves, mode),
    expressionNameFor,
    ortbVersion ?? null,
    floorBound,
  );
  /**
   * **The body's findings addressed the way every other surface addresses one** — `ref`, not `path`.
   *
   * The conversion existed inline at the `ValidationSummary` call and nowhere else, so the row mark
   * would have been a second one. Two spellings of *which row is this finding about* is how a dot
   * comes to appear where the Fix button cannot go, or the reverse. Order is preserved because
   * `onFix` indexes into it.
   */
  const addressedIssues: MappingIssue[] = [
    ...bodyIssues.map((i) => ({
      kind: i.kind,
      message: i.message,
      ref: i.path,
      fixable: !!(i.fix && i.fix.length),
      // The nested location travels too (`3i`): the detail page's list kept it and this one dropped
      // it, so a finding inside a leaf's expression could not mark the field it is about here.
      ...(i.at ? { at: i.at } : {}),
    })),
    ...conflictIssues,
  ];
  /**
   * **The tree opens by `LP14`'s rule** (`#1453`): a group with fewer than five fields opens, a larger
   * one is shut, and one holding a finding opens whatever its size, so an operator does not have to
   * open a group to learn it is wrong. Decided once, on the first draw, from the findings that draw
   * computed; a Fix or Review still opens the group it names.
   */
  if (collapsedState === null) {
    firstDraw = initiallyCollapsedSections(
      allGroups,
      addressedIssues.map((issue) => issue.ref ?? ''),
      readRememberedCollapsed(rememberKey),
    );
  }
  const collapsed = collapsedState ?? firstDraw;
  // Frozen after the first draw: a group reaching five fields while it is being edited must not shut.
  const decided = collapsed;
  React.useEffect(() => {
    setCollapsedState((prev) => prev ?? decided);
    // The first draw's decision only; later draws are the operator's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The viewer's arrangement, kept for the session under `rememberKey` once it has been decided.
  const drawnSections = allGroups.map((g) => g.section).join('\u0000');
  React.useEffect(() => {
    if (collapsedState !== null) writeRememberedCollapsed(rememberKey, drawnSections.split('\u0000'), collapsedState);
  }, [collapsedState, rememberKey, drawnSections]);
  const allCollapsed = allGroups.length > 0 && allGroups.every((g) => collapsed.has(g.section));
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(allGroups.map((g) => g.section)));

  const deleteSection = (section: string) => {
    emit(
      // `sectionOfLeaf`, not `path[0]` (`#1253`): the root group's leaves carry their own name at
      // `path[0]`, so a raw comparison keeps every one of them and deleting the envelope deletes
      // nothing.
      leaves.filter((l) => sectionOfLeaf(l) !== section),
      mode,
    );
    setPendingSections((prev) => prev.filter((s) => s !== section));
    if (addingTo === section) setAddingTo(null);
  };

  const addSection = (section: string) => {
    if (!section) return;
    if (!presentSections.has(section)) {
      setPendingSections((prev) => (prev.includes(section) ? prev : [...prev, section]));
    }
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.delete(section);
      return next;
    });
    setAddingTo(section);
    setNewKey('');
    setAddScope({ kind: 'each' });
  };

  const applyPaste = (text: string = pasteText) => {
    setPasteError(null);
    setPasteQuoteFix(false);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      // A very common cause is a paste from a doc/notes/chat app that rewrote
      // the straight quotes as curly ones. Lead with that diagnosis and offer a
      // one-click fix instead of the bare parser message (design R3).
      const { fixable } = diagnoseTypographicQuotes(text);
      if (fixable) {
        setPasteQuoteFix(true);
        setPasteError(
          'This looks like it was pasted from a document that replaced the straight quotes (") with curly ones (“ ”), which JSON does not accept.',
        );
      } else {
        setPasteError(`Invalid JSON: ${err instanceof Error ? err.message : String(err)}`);
      }
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setPasteError('Sample must be a JSON object.');
      return;
    }
    // The driver sets these per request from the session/pod — templating them
    // pins a constant `id` and replaces the dynamically-sized `imp` array, so we
    // skip them on import. Operators can still Add them explicitly if needed.
    // **Per field, with a reason for each** (`#1187`). This filtered by *section* — everything
    // under `id` and `imp` went, which took a partner's `tagid`, `protocols` and `minduration`
    // with it — and reported only that it had *"skipped driver-managed field(s): id, imp"*, naming
    // the sections rather than what it had taken, claiming *"the driver sets these per request"*
    // about fields the driver sets nothing at, and leaving no way back: `setPasteText('')` cleared
    // the only remaining copy.
    // Identical impressions collapse to one, and each note is stated once — `classifyPastedBody`.
    const { leaves: mapped, notes } = classifyPastedBody(parsed as Record<string, unknown>, ortbVersion ?? undefined);

    emit(mapped, mode);
    setRawSeed((s) => s + 1); // reflect the pasted body in the uncontrolled raw editor
    setPasteNotes(notes);
    setPasteOpen(false);
    setPasteText('');
  };

  /** Put back what the sample carried in place of a detected mapping (`#1001`). */
  const revertLeaf = (label: string) => {
    emit(
      leaves.map((l) => (pathToLabel(l.path) === label ? revertDetectedLeaf(l) : l)),
      mode,
    );
    setRawSeed((n) => n + 1); // reflect the literal in the uncontrolled raw editor
  };

  /** The way back to the template's current leaf, for a leaf changed from it — see `fromTemplate`. */
  const templateRevertFor = (leaf: MappingLeaf, label: string): TemplateRevert | null => {
    if (readOnly || leaf.provenance?.kind !== 'overridden') return null;
    const fromIt = fromTemplate?.leaves.find((l) => pathToLabel(l.path) === label);
    if (!fromTemplate || !fromIt) return null;
    return {
      templateName: fromTemplate.name,
      onRevert: () => {
        if (editing === label) {
          setEditing(null);
          setEditSnapshot(null);
        }
        emit(
          leaves.map((l) => (pathToLabel(l.path) === label ? { ...fromIt, provenance: { kind: 'template' } } : l)),
          mode,
        );
        setRawSeed((n) => n + 1);
      },
    };
  };

  /**
   * **What is still automatic, and whether the panel has re-announced itself.**
   *
   * Closed by default and re-opened only when the *contents* change — keyed on the paths rather
   * than the count, because a paste can author one automatic field and leave the count alone,
   * which is exactly the case worth showing. The first render is skipped deliberately: arriving
   * at a source is not an event that changed anything, and treating it as one is the defect this
   * panel's VAST twin was found to have.
   */
  const [autoOpen, setAutoOpen] = React.useState(false);
  const [autoWhenKnown, setAutoWhenKnown] = React.useState(false);
  const [openAutoSections, setOpenAutoSections] = React.useState<string[]>([]);
  const autoSent = React.useMemo(() => autoSentGroups(leaves, mode, autoWhenKnown), [leaves, mode, autoWhenKnown]);
  const autoSentCount = autoSent.reduce((n, g) => n + g.fields.length, 0);
  /** How many more there are to see, so the checkbox can say what it costs. */
  const autoWhenKnownCount = React.useMemo(() => {
    const all = autoSentGroups(leaves, mode, true).reduce((n, g) => n + g.fields.length, 0);
    return all - autoSentGroups(leaves, mode, false).reduce((n, g) => n + g.fields.length, 0);
  }, [leaves, mode]);

  /**
   * **A removed parent leaf is listed once, as a removal** (`#1379`) — its line says what goes out in
   * its place, so the plain line for the same path would be a second answer about one field.
   */
  const removedLabels = React.useMemo(() => new Set(parentRemovals?.names ?? []), [parentRemovals]);
  const autoSentShown = React.useMemo(
    () =>
      autoSent
        .map((g) => ({ ...g, fields: g.fields.filter((f) => !removedLabels.has(f.path)) }))
        .filter((g) => g.fields.length > 0),
    [autoSent, removedLabels],
  );
  const removalCount = removedLabels.size;
  const autoSentKey = [
    ...autoSent.flatMap((g) => g.fields.map((f) => f.path)),
    ...[...removedLabels].map((l) => `removed:${l}`),
  ].join(',');
  const lastAutoKey = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (lastAutoKey.current !== null && lastAutoKey.current !== autoSentKey && autoSentKey !== '') setAutoOpen(true);
    lastAutoKey.current = autoSentKey;
  }, [autoSentKey]);

  /**
   * Promote a driver-built field into an authored row — the panel's per-row Edit, and *"the
   * precise version of the same action"* that replaced add-recommended on the VAST side (#1013:
   * a button proposing what already happens).
   *
   * The merge base speaks dotted paths with indices collapsed; `addFieldByPath` wants a section
   * and a section-relative path, and `buildFieldPath` puts the index back where the section is an
   * array. One split, here, rather than a second address vocabulary.
   */
  const addFieldByMergeBasePath = (path: string) => {
    const [section, ...rest] = path.split('.');
    if (!section) return;
    /**
     * **A top-level field has no dot, and this used to `return` on it** (`#1253`) — so `Edit` beside
     * every auto-sent envelope field (`id`, `at`, `cur`, `test`, `tmax`) was an enabled, styled,
     * inert control. *A no-op handler is a live control that does nothing, and that is worse than a
     * disabled one.*
     *
     * The dotless case is the **root** group: its section contributes no path segment, so the whole
     * path is the relative one.
     */
    if (rest.length === 0) {
      addFieldByPath(ORTB_ROOT_SECTION, section);
      return;
    }
    addFieldByPath(section, rest.join('.'));
  };

  /**
   * Section-relative path with array indices dropped, e.g. `imp[0].video.w` → `video.w`.
   *
   * **A root field's relative path is the whole of it** (`#1253`): the synthetic `request` group
   * contributes no segment, so dropping the first one here would leave `tmax` with an empty address
   * and the picker would offer it again beside itself. The length check this used covered a root
   * scalar and not a root **array** — `bcat[0]` sliced to `[0]` and then to nothing — so it now reads
   * the one rule the row label reads too (`pathWithinSection`).
   */
  const relPathOf = (leaf: MappingLeaf): string => catalogSegments(pathWithinSection(leaf)).join('.');
  /**
   * The fields a section already carries, by catalog address. For `imp` only **every impression**
   * counts (RS4c): a field set on the last impression alone is not present on the others, so the
   * recommended set still offers it for all of them.
   */
  const sectionPresentRelPaths = (section: string): Set<string> =>
    new Set(
      leaves
        .filter((l) => sectionOfLeaf(l) === section)
        .filter((l) => {
          const scope = patternScopeOf(l.path);
          return !scope || scope.kind === 'each';
        })
        .map(relPathOf),
    );

  const defaultLeafFor = (section: string, relPath: string, scope?: PatternScope): MappingLeaf =>
    sharedDefaultLeafFor(section, relPath, leaves, scope);

  const addFieldByPath = (section: string, relPath: string, focus = true, scope?: PatternScope) => {
    const key = relPath.trim();
    if (!key) return;
    const seeded = defaultLeafFor(section, key, scope);
    // **Secret is suggested as the field is added, from its own name** (`CR2`, `#1371`) — the same rule
    // a header or a parameter row gets as it is named, and only on an empty literal the operator can
    // still untick.
    const lastSegment = String(seeded.path[seeded.path.length - 1] ?? '');
    const leaf: MappingLeaf =
      seeded.kind === 'static' && (seeded.staticValue ?? '') === '' && suggestsSecretValue(lastSegment)
        ? { ...seeded, secret: true }
        : seeded;
    const label = pathToLabel(leaf.path);
    if (leaves.some((l) => pathToLabel(l.path) === label)) return; // already present
    emit([...leaves, leaf], mode);
    setPendingSections((prev) => prev.filter((s) => s !== section));
    setAddingTo(null);
    setNewKey('');
    if (focus) {
      // Added and opened in one click, so Cancel takes it away again (`#1262`). `addRecommended`
      // passes `focus: false` and adds without opening anything, so its fields are never `created`
      // for this purpose — which is right: nothing is open to cancel out of.
      setEditSnapshot({ row: leaf, origin: 'created' });
      setEditing(label);
    }
  };
  const addField = (section: string) => {
    if (addScope) addFieldByPath(section, newKey, true, addScope);
  };

  /** Add all missing required/recommended standard fields for a section, pre-mapped. */
  const addRecommended = (section: string) => {
    const present = sectionPresentRelPaths(section);
    const missing = getMissingStandardFields(section, present, ortbVersion, mode);
    if (missing.length === 0) return;
    const existingLabels = new Set(leaves.map((l) => pathToLabel(l.path)));
    const toAdd = missing
      .map((f) => defaultLeafFor(section, f.name))
      .filter((l) => !existingLabels.has(pathToLabel(l.path)));
    if (toAdd.length === 0) return;
    emit([...leaves, ...toAdd], mode);
    setPendingSections((prev) => prev.filter((s) => s !== section));
    setAddingTo(null);
  };

  /**
   * Empty-state one-click: seed a sensible baseline CTV/OTT BidRequest — each
   * starter section's required + recommended fields, pre-mapped to macros where
   * one fits. A starting point the operator refines, not a finished body.
   */
  const addRecommendedBody = () => {
    const toAdd = buildStarterLeaves(mode, ortbVersion, leaves);
    if (toAdd.length === 0) return;
    emit([...leaves, ...toAdd], mode);
    setPendingSections([]);
  };

  /**
   * Resolve a validation issue (or all of them) by adding the fields it names,
   * default-mapped exactly like Add field / Add recommended — a `{macro}` where
   * one fits, else a type-correct empty literal — or by moving a field authored at
   * a location this version does not read (`movedFrom`).
   */
  const addFixLeaves = (fixes: OrtbFieldRef[]) => {
    // Shared with the readiness panel's staged fix (#899) — see `leavesWithFixes`.
    const next = leavesWithFixes(leaves, fixes);
    if (next === leaves) return;
    const fixedSections = new Set(fixes.map((f) => f.section));
    emit(next, mode);
    setPendingSections((prev) => prev.filter((s) => !fixedSections.has(s)));
    setRawSeed((s) => s + 1); // reflect the fix in the uncontrolled raw editor
  };
  const fixAllRefs = bodyIssues.flatMap((i) => i.fix ?? []);

  /**
   * **Rename the open row** (Todd, 2026-09-28) — a move of one leaf, through the same `leavesMoved` a
   * version Fix makes. The open row follows its new name, and its snapshot still holds the old path, so
   * Cancel puts the name back with everything else and Apply keeps it. Returns why a name is refused.
   */
  const renameOpenLeaf = (leaf: MappingLeaf, nextName: string): string | null => {
    const result = renameLeaf(leaves, leaf, nextName);
    if (!result.ok) return result.problem;
    if (result.leaves !== leaves) {
      emit(result.leaves, mode, { live: true });
      setEditing(result.label);
      setRawSeed((n) => n + 1);
    }
    return null;
  };

  const commitCustomSection = () => {
    const s = customSection.trim();
    if (s) addSection(s);
    setCustomSection('');
    setAddingCustomSection(false);
  };

  return (
    <Box
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="lg"
      bg="bg.card"
      overflow="hidden"
      // Keep Enter **and Escape** inside the editor (add a field, accept a completion, insert a
      // newline in the raw editor, clear the filter) — never let either bubble up to the wizard,
      // which advances a step on one and closes on the other.
      //
      // Escape was added 2026-08-21 (#1129): this guard shipped covering Enter only, and the
      // section filter's own `Escape` → clear then reached the dialog and **closed the wizard**,
      // discarding a part-built source. The same hole existed on the parameter and header editors
      // and is closed the same way. One key handled and the other not is the shape to watch for —
      // the guard looked present.
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== 'Escape') return;
        e.stopPropagation();
        // Escape closes the open leaf, matching the parameter and header editors and matching what
        // Cancel does — `cancelEdit` restores the snapshot taken when the editor opened, so this
        // reverts rather than discards. Swallowing the key without giving it a meaning is its own
        // surprise (#1129): it had an effect before, and doing nothing reads as broken.
        if (e.key === 'Escape' && editing) cancelEdit();
      }}
    >
      {/* header */}
      <HStack px={4} py={3} borderBottomWidth="1px" borderColor="border.default" gap={3} flexWrap="wrap">
        <VStack align="start" gap={0} mr="auto">
          <Text fontSize="xs" fontFamily="mono" letterSpacing="wide" textTransform="uppercase" color="fg.muted">
            Request decoration · openrtb
          </Text>
          <Text fontWeight="600" color="fg.heading">
            Request body
          </Text>
        </VStack>
        {!readOnly && (
          <HStack gap={1} bg="bg.subtle" borderWidth="1px" borderColor="border.default" borderRadius="full" p="3px">
            {(['guided', 'raw'] as View[]).map((v) => (
              <Button
                key={v}
                size="xs"
                variant="ghost"
                borderRadius="full"
                onClick={() => chooseView(v)}
                bg={view === v ? 'accent.solid' : 'transparent'}
                color={view === v ? 'fg.onAccent' : 'fg.muted'}
                _hover={{ bg: view === v ? 'accent.solid' : 'bg.card' }}
              >
                {v === 'guided' ? 'Guided' : 'Raw JSON'}
              </Button>
            ))}
          </HStack>
        )}
        <HStack gap={1} fontSize="xs" color="fg.muted">
          <Text>mode</Text>
          {readOnly ? (
            <Text fontFamily="mono" color="fg.default">
              {mode}
            </Text>
          ) : (
            <NativeSelect.Root size="xs" width="auto">
              <NativeSelect.Field
                value={mode}
                onChange={(e) => emit(leaves, e.target.value as 'merge' | 'replace')}
                aria-label="Body merge mode"
                bg="bg.card"
                color="fg.default"
              >
                <option value="merge">merge</option>
                <option value="replace">replace</option>
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          )}
        </HStack>
        {!readOnly && onQuickTest && (
          <Button size="xs" bg="accent.solid" color="fg.onAccent" _hover={{ bg: 'accent.ink' }} onClick={onQuickTest}>
            Quick test
          </Button>
        )}
      </HStack>

      {/* **What the driver supplies without being asked** (§15, #1117).
          This was a paragraph of prose, and prose is where the claim rotted: it said the driver
          builds *"the whole video object"* and named `ORTB_DRIVER_MERGE_BASE.device` — a
          section-grained map whose `imp: '*'` was measurably false (no `video.playbackend` is
          built at all). It is a list now, per field, read from a declaration that a spec asserts
          against real requests. */}
      <Box
        px={4}
        py={2.5}
        bg={mode === 'merge' ? 'info.tint' : 'warning.tint'}
        borderBottomWidth="1px"
        borderColor="border.default"
      >
        {/* The heading is state — it changes with the mode — and stays. What the mode means is
            teaching, true on every visit, and moved behind the `?` beside it (#1179). */}
        <HStack gap={1} align="center">
          <Text fontSize="xs" color={mode === 'merge' ? 'info.ink' : 'warning.ink'} fontWeight="600">
            {mode === 'merge' ? 'The driver already builds most of this' : 'You own the entire bid request'}
          </Text>
          <FieldHelpTooltip
            ariaLabel={mode === 'merge' ? 'About merge mode' : 'About replace mode'}
            content={
              <Text fontSize="xs">
                {mode === 'merge' ? (
                  <>
                    Fields you add here{' '}
                    <Text as="span" fontWeight="600">
                      merge over
                    </Text>{' '}
                    what the driver builds from the session, so adding one{' '}
                    <Text as="span" fontWeight="600">
                      overrides
                    </Text>{' '}
                    its value — set{' '}
                    <Text as="span" fontFamily="mono">
                      video.maxduration
                    </Text>{' '}
                    to a constant and it stops following the pod. Author what the driver cannot know: privacy, supply
                    chain, and the identifiers your exchange requires.
                  </>
                ) : (
                  <>
                    Replace mode discards everything the driver builds, so every required field must be authored here —
                    including{' '}
                    <Text as="span" fontFamily="mono">
                      imp.id
                    </Text>
                    ,{' '}
                    <Text as="span" fontFamily="mono">
                      video.mimes
                    </Text>
                    ,{' '}
                    <Text as="span" fontFamily="mono">
                      video.maxduration
                    </Text>{' '}
                    and{' '}
                    <Text as="span" fontFamily="mono">
                      video.protocols
                    </Text>
                    . Nothing is derived from the session unless you map it.
                  </>
                )}
              </Text>
            }
          />
        </HStack>
      </Box>

      {/* **Sent automatically** — the ORTB half of the panel the VAST path has had since #951 · R5.
          Ghosted, so the eye reads the tree below as the operator's and these as the system's;
          **closed by default**, because *"these go out whether or not you author them"* is news
          exactly once — when the set changes — and a panel that re-announces itself on every visit
          is one an operator learns to collapse without reading. A paste is what materially changes
          which fields are still automatic, so that re-opens it and a re-render does not.

          **Grouped by section, and `always` alone until asked.** Flat and complete this is 79 rows;
          the headline claim — *if you author nothing, this is what gets sent* — is true of 22 of
          them, and the rest answer a different question. Sections are the tree's own structure, so
          the panel reads the same way as the thing it describes, and the `site`/`app` pair needs no
          collapsing because the two halves were never in one place. */}
      {(autoSentCount > 0 || autoWhenKnownCount > 0 || removalCount > 0) && (
        <Box borderBottomWidth="1px" borderColor="border.default" bg="bg.subtle">
          <HStack
            as="button"
            w="100%"
            px={4}
            py={2}
            gap={2}
            textAlign="left"
            aria-expanded={autoOpen}
            onClick={() => setAutoOpen((o) => !o)}
            _hover={{ bg: 'bg.muted' }}
          >
            <Text fontSize="xs" color="fg.muted" flexShrink={0}>
              {autoOpen ? '▾' : '▸'}
            </Text>
            <Text fontSize="xs" fontWeight="600" color="fg.muted">
              Sent automatically
            </Text>
            <Text fontSize="xs" color="fg.subtle">
              If you author nothing, the driver still sends {autoSentCount} field
              {autoSentCount === 1 ? '' : 's'}
              {removalCount > 0 ? ` · ${removalCount} of ${parentName ?? 'the parent'}'s removed here` : ''}
            </Text>
          </HStack>

          {autoOpen && (
            <VStack align="stretch" gap={0} pb={2}>
              <HStack px={4} pb={1.5} gap={3} flexWrap="wrap">
                <Text fontSize="xs" color="fg.subtle" mr="auto">
                  Edit one to override or suppress it.
                </Text>
                {autoWhenKnownCount > 0 && (
                  <HStack
                    as="label"
                    gap={1.5}
                    cursor="pointer"
                    fontSize="xs"
                    color="fg.muted"
                    flexShrink={0}
                    title="Fields the driver sends when the session carries them — a viewer's IP, a consent string, the content title"
                  >
                    <input
                      type="checkbox"
                      checked={autoWhenKnown}
                      onChange={(e) => setAutoWhenKnown(e.target.checked)}
                    />
                    <Text as="span">Include {autoWhenKnownCount} more sent when known</Text>
                  </HStack>
                )}
              </HStack>

              {/* The parent's leaves this source stopped overriding (`#1379`), first: they are the
                  lines this source authored, where everything below is the driver's alone. */}
              <ParentRemovalRows
                removals={parentRemovals}
                parentName={parentName}
                readOnly={readOnly}
                insteadOf={(label) => bodyRemovalReplacement(label, ortbVersion)}
                revert={revertFromParent ?? null}
                revertsNow={baselineLeaves !== undefined}
                isReverting={isReverting}
                pathsOf={(label) => [bodyRemovalPath(label)]}
                undo={undoStopOverriding}
              />
              {autoSentShown.map((group) => {
                const open = openAutoSections.includes(group.section);
                return (
                  <Box key={group.section}>
                    <HStack
                      as="button"
                      w="100%"
                      px={4}
                      py={1}
                      gap={2}
                      textAlign="left"
                      _hover={{ bg: 'bg.card' }}
                      onClick={() =>
                        setOpenAutoSections((prev) =>
                          prev.includes(group.section)
                            ? prev.filter((s) => s !== group.section)
                            : [...prev, group.section],
                        )
                      }
                    >
                      <Text fontSize="xs" color="fg.subtle" flexShrink={0}>
                        {open ? '▾' : '▸'}
                      </Text>
                      {/* The same heading in the same editor, so the same signal (`#1253`): this
                          panel synthesised `request` first and is where the name came from. */}
                      <Text
                        fontFamily="mono"
                        fontSize="xs"
                        color="fg.muted"
                        flexShrink={0}
                        fontStyle={group.section === ORTB_ROOT_SECTION ? 'italic' : 'normal'}
                      >
                        {group.section}
                      </Text>
                      <Text fontSize="xs" color="fg.subtle">
                        {group.fields.length}
                        {/* The split, where there is one — a section that is entirely `always`
                            should not have to say so twice. */}
                        {group.alwaysCount < group.fields.length ? ` · ${group.alwaysCount} always` : ''}
                      </Text>
                    </HStack>
                    {open &&
                      group.fields.map((field) => (
                        <HStack key={field.path} pl={10} pr={4} py={1} gap={3} _hover={{ bg: 'bg.card' }}>
                          <Text fontFamily="mono" fontSize="xs" color="fg.muted" minW="140px" maxW="230px" truncate>
                            {field.path}
                          </Text>
                          <Text fontSize="xs" color="fg.subtle" flex="1" truncate title={field.from}>
                            {field.from}
                          </Text>
                          {/* *when known* is not a hedge — it is the difference between a field an
                              exchange can rely on and one that follows the session. Privacy says so
                              in its own words, because *we never invent one* is the part that
                              matters there (#912). */}
                          {field.certainty !== 'always' && (
                            <Text fontSize="10px" color="fg.subtle" flexShrink={0} whiteSpace="nowrap">
                              {field.certainty === 'forwarded-if-sent' ? 'if the publisher sent one' : 'when known'}
                            </Text>
                          )}
                          {!readOnly && (
                            <Button
                              size="xs"
                              variant="ghost"
                              color="accent.solid"
                              flexShrink={0}
                              onClick={() => addFieldByMergeBasePath(field.path)}
                            >
                              Edit
                            </Button>
                          )}
                        </HStack>
                      ))}
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>
      )}

      {/* auto-parse subbar */}
      {!readOnly && (
        <HStack
          px={4}
          py={2.5}
          bg="bg.subtle"
          borderBottomWidth="1px"
          borderColor="border.default"
          gap={3}
          flexWrap="wrap"
        >
          <Text fontSize="sm" color="fg.muted" mr="auto">
            Paste a sample BidRequest to auto-map session fields.
          </Text>
          {/* One vocabulary with the band below (`#1254`): this is the same number under a second
              word, and *unresolved* was wrong for half of what it counts — an empty static is
              **not set**, not unresolved. `won't resolve` is true of both. */}
          <Text fontSize="xs" fontFamily="mono" color="fg.muted">
            {leaves.length} fields{unresolvedCount > 0 ? ` · ${unresolvedCount} won't resolve` : ''}
          </Text>
          <Button
            size="xs"
            variant="outline"
            borderColor="border.default"
            color="accent.solid"
            onClick={() => setPasteOpen((o) => !o)}
          >
            Paste sample…
          </Button>
        </HStack>
      )}

      {!readOnly && pasteOpen && (
        <Box px={4} py={3} borderBottomWidth="1px" borderColor="border.default">
          <Textarea
            value={pasteText}
            onChange={(e) => {
              setPasteText(e.target.value);
              if (pasteError) setPasteError(null);
              if (pasteQuoteFix) setPasteQuoteFix(false);
            }}
            placeholder='{ "device": { "w": 1920, "ip": "…" }, "regs": { "ext": { "us_privacy": "1YNN" } } }'
            bg="bg.card"
            color="fg.default"
            borderColor="border.default"
            fontFamily="mono"
            fontSize="xs"
            minH="120px"
          />
          {pasteError && (
            <HStack mt={1} gap={2} align="flex-start">
              <Text fontSize="xs" color="error.ink">
                {pasteError}
              </Text>
              {pasteQuoteFix && (
                <Button
                  size="xs"
                  variant="outline"
                  flexShrink={0}
                  onClick={() => {
                    const { normalized } = diagnoseTypographicQuotes(pasteText);
                    setPasteText(normalized); // show the corrected text, then parse it
                    applyPaste(normalized);
                  }}
                >
                  Fix quotes &amp; parse
                </Button>
              )}
            </HStack>
          )}
          <HStack justify="flex-end" gap={2} mt={2}>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => {
                setPasteOpen(false);
                setPasteError(null);
                setPasteQuoteFix(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="xs"
              bg="accent.solid"
              color="fg.onAccent"
              _hover={{ bg: 'accent.ink' }}
              onClick={() => applyPaste()}
            >
              Parse & map
            </Button>
          </HStack>
        </Box>
      )}

      {pasteNotes.length > 0 && (
        <VStack
          align="stretch"
          px={4}
          py={2.5}
          bg="info.tint"
          color="info.ink"
          fontSize="sm"
          gap={1.5}
          borderBottomWidth="1px"
          borderColor="border.default"
        >
          <HStack gap={2} align="start">
            <Text flex="1" fontWeight="600">
              {pasteNotes.length} field{pasteNotes.length === 1 ? '' : 's'} from your sample
              {pasteNotes.length === 1 ? ' was' : ' were'} not imported as written
            </Text>
            <Button size="xs" variant="ghost" color="info.ink" flexShrink={0} onClick={() => setPasteNotes([])}>
              Dismiss
            </Button>
          </HStack>
          {/* One line per field, because the operator's question is *what happened to mine* and a
              summary cannot answer it. This is the shape the VAST paste has had since #952. */}
          {pasteNotes.map((note) => (
            <Text key={note} fontSize="xs" color="info.ink">
              {note}
            </Text>
          ))}
        </VStack>
      )}

      {!readOnly && unresolvedCount > 0 && (
        <HStack
          px={4}
          py={2.5}
          bg="warning.tint"
          color="warning.ink"
          fontSize="sm"
          gap={2}
          borderBottomWidth="1px"
          borderColor="border.default"
        >
          {/**
           * **It names its own subject, so it cannot be read as the heading of the list below it**
           * (`#1254`). It said *"1 field needs attention"* directly above `ValidationSummary`'s
           * *"2 WARNINGS"*, counted a different set, and read as that panel's heading — two honest
           * counts of two different questions, stacked with nothing saying they were different.
           *
           * **And the consequence it stated was wrong for half of what it counts.** *"They'll send
           * as-is until set"* is true of an empty value and false of an unknown macro: the driver
           * treats an unresolved leaf as `UNRESOLVED` and **omits the key**, keeping whatever it
           * derived for that path itself (`openrtb.driver.ts`, `#880`). One sentence, two causes,
           * and the outcome given was the one that does not apply to a macro typo — which is the
           * case an operator is most likely to be in when they read it.
           */}
          <Text fontWeight="600">
            {unresolvedCount} of {leaves.length} field{leaves.length === 1 ? '' : 's'} won't resolve
          </Text>
          <Text>
            — an unknown macro, or a value that was never set. An unknown macro leaves the key off the request and keeps
            whatever the driver derived; a value never set is sent empty.
          </Text>
        </HStack>
      )}

      {/**
       * **The bulk add, out of the findings banner and named for the act** (`#1254`).
       *
       * It was `Fix all` inside `ValidationSummary`, which tied *add the fields the checks name* —
       * an editor capability — to a list the page now draws instead, so it would have vanished on
       * the detail page with the banner. It renders on **both** surfaces now, which it did not
       * before, and it says what it does: `Fix all` beside a list the operator cannot see is a
       * button with no subject.
       *
       * `fixAllRefs` is every field a finding names as missing — required and recommended — so the
       * count is fields rather than findings, which is what pressing it changes.
       */}
      {!readOnly && allGroups.length > 0 && fixAllRefs.length > 0 && (
        <HStack px={4} py={2} gap={3} borderBottomWidth="1px" borderColor="border.default" flexWrap="wrap">
          <Button
            size="xs"
            bg="accent.solid"
            color="fg.onAccent"
            _hover={{ bg: 'accent.ink' }}
            onClick={() => addFixLeaves(fixAllRefs)}
          >
            Add {fixAllRefs.length} missing field{fixAllRefs.length === 1 ? '' : 's'}
          </Button>
          <Text fontSize="xs" color="fg.muted">
            the required and recommended fields the configuration checks name
          </Text>
        </HStack>
      )}

      {!readOnly && allGroups.length > 0 && bodyIssues.length + conflictIssues.length > 0 && (
        <Box px={4} py={2} borderBottomWidth="1px" borderColor="border.default">
          <ValidationSummary
            issues={addressedIssues}
            onFix={(issue, i) => {
              // Conflicts follow the body's own findings; their Fix opens the leaf (`#944`).
              if (i >= bodyIssues.length) {
                if (issue.ref && holdsLeaf(issue.ref)) openEditor(issue.ref);
                return;
              }
              const fx = bodyIssues[i].fix;
              if (fx && fx.length) addFixLeaves(fx);
            }}
            review={findingReview}
          />
        </Box>
      )}

      {/* guided view */}
      {effectiveView === 'guided' && (
        <Box py={1}>
          {/* A second paragraph saying the merge banner's sentence — *the driver builds the rest,
              adding a field overrides it* — was here, always on in merge mode. Deleted rather than
              moved (#1179): the banner's heading states it and its `?` explains it. */}
          {allGroups.length > 0 && (
            <HStack px={4} pt={1} gap={2} justify="flex-end">
              {/* Leaf filter (#922 · 4). Matched against the full dotted label, which is
                  the same address the catalog, the validator and the resolution mark use
                  — so what an operator types here is what they read everywhere else. */}
              <Input
                size="xs"
                maxW="260px"
                bg="bg.subtle"
                fontFamily="mono"
                value={filter}
                placeholder="Filter fields — imp.video.plcmt"
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && setFilter('')}
                aria-label="Filter body fields"
              />
              {filtering && (
                <>
                  <Text fontSize="xs" color={filterMatchCount === 0 ? 'warning.ink' : 'fg.muted'} whiteSpace="nowrap">
                    {filterMatchCount === 0 ? 'no match' : `${filterMatchCount} of ${leaves.length}`}
                  </Text>
                  <Button size="xs" variant="ghost" color="fg.muted" onClick={() => setFilter('')}>
                    Clear
                  </Button>
                </>
              )}
              {/* Expand/collapse is meaningless while filtering — a match's section is
                  open by definition, and collapsing one would hide the thing searched
                  for. Hidden rather than disabled: a control that cannot act is noise. */}
              {!filtering && (
                <Button size="xs" variant="ghost" color="fg.muted" onClick={allCollapsed ? expandAll : collapseAll}>
                  {allCollapsed ? 'Expand all' : 'Collapse all'}
                </Button>
              )}
            </HStack>
          )}
          {/* A filter matching nothing must say so. The list simply going empty reads
              as "this source has no body fields", which is a different and alarming
              claim — and the one the operator would act on. */}
          {filtering && filterMatchCount === 0 && (
            <Box px={4} py={6}>
              <Text fontSize="xs" color="fg.muted" textAlign="center">
                No field matches{' '}
                <Text as="span" fontFamily="mono" color="fg.default">
                  {filter.trim()}
                </Text>
                . The body has {leaves.length} field{leaves.length === 1 ? '' : 's'} — clear the filter to see them.
              </Text>
            </Box>
          )}
          {allGroups.length === 0 && (
            <VStack py={readOnly ? 6 : 10} gap={3}>
              <VStack gap={1}>
                <Text color="fg.default" fontSize="sm" fontWeight="600">
                  No body fields yet
                </Text>
                <Text color="fg.muted" fontSize="xs" maxW="sm" textAlign="center">
                  {readOnly
                    ? 'The driver builds the BidRequest from the session; no custom body overrides are set.'
                    : mode === 'replace'
                      ? 'Start from a full recommended CTV video body — imp, app, device, privacy and source, pre-mapped to session macros — or paste a sample BidRequest to auto-map it.'
                      : 'In merge mode the driver already builds imp, device and app/site from the session. Seed the fields it can’t derive — privacy (regs) and supply chain (source) — or paste a sample to auto-map it.'}
                </Text>
              </VStack>
              {!readOnly && (
                <HStack gap={2}>
                  <Button
                    size="xs"
                    bg="accent.solid"
                    color="fg.onAccent"
                    _hover={{ bg: 'accent.ink' }}
                    onClick={addRecommendedBody}
                  >
                    Add recommended body
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    borderColor="border.default"
                    color="accent.solid"
                    onClick={() => setPasteOpen(true)}
                  >
                    Paste a sample BidRequest
                  </Button>
                </HStack>
              )}
            </VStack>
          )}
          {/* One legend for the whole body, not one per section (R4): the sections
              are groupings of one surface, and a legend repeated per section is the
              same noise as a tooltip per row. */}
          <ProvenanceLegend values={leaves.map((l) => l.provenance)} />
          {visibleGroups.map((group) => {
            // A section with a match opens for the duration of the search without its
            // collapse state being rewritten — clearing the box restores the operator's
            // own arrangement exactly.
            const isCollapsed = !filtering && collapsed.has(group.section);
            const sectionPresent = sectionPresentRelPaths(group.section);
            const missingStd = getMissingStandardFields(group.section, sectionPresent, ortbVersion, mode);
            const fieldSuggestions =
              addingTo === group.section
                ? getFieldSuggestions(group.section, newKey, sectionPresent, ortbVersion ?? undefined).slice(0, 8)
                : [];
            // The field this version reads, where the other version's is authored: offered as a move (Todd,
            // 2026-09-28), since adding it beside the authored one would send the signal twice (`#1315`).
            const moveOffers =
              addingTo === group.section ? getMoveOffers(group.section, newKey, sectionPresent, ortbVersion) : [];
            return (
              // The section's own anchor: a finding can name a section rather than a leaf ("both
              // `site` and `app` are set"), and Review lands on its header rather than nowhere.
              <Box key={group.section} id={rowAnchorId('ortbBody', group.section)} px={4} py={1}>
                <HStack py={2} gap={2} justify="space-between">
                  <HStack
                    as="button"
                    onClick={() => toggleSection(group.section)}
                    gap={2}
                    flex="1"
                    minW="0"
                    role="button"
                    aria-expanded={!isCollapsed}
                  >
                    <Text
                      fontFamily="mono"
                      fontSize="xs"
                      color="fg.muted"
                      transform={isCollapsed ? 'rotate(-90deg)' : 'none'}
                      transition="transform 0.15s"
                    >
                      ▾
                    </Text>
                    {/**
                     * **The synthetic heading is italic, and it still says what it means** (`#1253`,
                     * Todd 2026-09-11: *"we'd need to indicate to the user that it is synthetic for
                     * display, and anything in the section appears at the root level"*, then
                     * *"perhaps it's displayed in italics too eg _request_"*).
                     *
                     * Every other heading here **is** a path segment — `imp`, `device`, `regs` — so a
                     * reader who knows the JSON would otherwise expect a `request` key in the body,
                     * and there is none.
                     *
                     * **Both, not either.** The italic is the glance-level signal, and it reaches
                     * sighted readers only: a font style is not in the accessible tree, and a reader
                     * using a screen reader would get the same word as every real section with
                     * nothing distinguishing it. That is *a use verdict is made at a screen* in
                     * miniature — the cue is right and it cannot carry the claim on its own. So the
                     * sentence stays, and it is what a reader of any kind gets.
                     */}
                    <Text
                      fontFamily="mono"
                      fontSize="sm"
                      color="fg.title"
                      fontWeight="500"
                      fontStyle={group.section === ORTB_ROOT_SECTION ? 'italic' : 'normal'}
                    >
                      {group.section}
                    </Text>
                    <Text fontFamily="mono" fontSize="xs" color="fg.muted">
                      · {group.leaves.length}
                    </Text>
                    {/* **A group holding a finding says so on its header**, open or shut (Todd,
                        2026-09-28, `#1412`): a shut group with no mark reads as *fine*. The section
                        header's mark (`LP9`) — the worst finding's dot, named by its sentence, and a
                        count past one. */}
                    <GroupFindingsMark
                      issues={addressedIssues.filter((issue) => groupHoldsRef(group, issue.ref ?? ''))}
                    />
                    {group.section === ORTB_ROOT_SECTION && (
                      <Text fontSize="xs" color="fg.muted">
                        — grouped for display; these sit at the top of the request
                      </Text>
                    )}
                    {/* **Said once, at the section** (RS4c): what the bracket on every row in it
                        means. `[*]` is the default for every impression the pod asks for, and a
                        numbered or `last` row overrides one — the order they are listed in. */}
                    {ORTB_PATTERN_SECTIONS.has(group.section) && (
                      <Text fontSize="xs" color="fg.muted">
                        — [*] is every impression the pod asks for; [n] and [last] override one
                      </Text>
                    )}
                    {/* **The section carries the statement when its rows cannot** (#1143).
                        `layerFor` replaces an array wholesale, so `imp` is one leaf in the layer and
                        nothing beneath it is addressable — a per-row chip there would either render
                        nothing at all or claim a granularity that does not exist. Saying it once,
                        here, is the M3 rule the reference graph already follows: a gap is reported
                        as a known blind spot, never rendered as nothing to worry about. */}
                    {isWholeSectionLeaf(inheritance, group.section) && (
                      <InheritanceMark
                        inheritance={inheritance}
                        path={ortbBodySectionPath(group.section)}
                        parentName={parentName}
                        isReverting={isReverting}
                        onRevert={onRevert}
                      />
                    )}
                  </HStack>
                  {!readOnly && (
                    <Button
                      size="xs"
                      variant="ghost"
                      color="fg.muted"
                      _hover={{ color: 'error.solid' }}
                      onClick={() => deleteSection(group.section)}
                      aria-label={`Remove ${group.section} section`}
                    >
                      Remove
                    </Button>
                  )}
                </HStack>

                {!isCollapsed && isWholeSectionLeaf(inheritance, group.section) && (
                  <Text fontSize="xs" color="fg.muted" pb={1}>
                    This whole section is inherited or overridden together — editing any field in it takes the rest with
                    it. Per-field inheritance here needs the array to be addressable field by field.
                  </Text>
                )}

                {!isCollapsed && (
                  <VStack align="stretch" gap={0}>
                    {orderedByScope(group.leaves).map((leaf) => {
                      const label = pathToLabel(leaf.path);
                      const sub = leafSummary(leaf, expressionNameFor);
                      const isEditing = editing === label;
                      // Relative path under the section, preserving array brackets
                      // (e.g. `imp` leaf → `[0].video.w`, not a mangled `0].video.w`). In the
                      // envelope the whole path is the field — `bcat[0]`, not `[0]`.
                      const rel = pathToLabel(pathWithinSection(leaf)) || label;
                      /* **A concept row is named for the receiver** (#1112). The stored path
                         is the identity — per-path attribution, inheritance and the mapping
                         document all key on it — so the spelling changes on screen and never
                         in the document, and the tooltip carries the address that was saved. */
                      const wireName = conceptWireLabelForPath(leaf.path, ortbVersion ?? undefined);
                      const displayRel = wireName ? rel.replace(/[^.]+$/, wireName) : rel;
                      const conceptResolution = conceptResolutionForLeaf(leaf, ortbVersion ?? undefined);
                      return (
                        <Box key={label} id={rowAnchorId('ortbBody', label)} ref={isEditing ? revealRef : undefined}>
                          <MappingRow
                            variant="flow"
                            readOnly={readOnly}
                            editing={isEditing}
                            summaryLabel={sub.label}
                            summaryKind={sub.kind}
                            onEdit={() => openEditor(label)}
                            onApply={applyEdit}
                            onCancel={cancelEdit}
                            layerAction={layerActionFor(
                              // **A parent's leaf over a field the driver builds can be removed here**
                              // (`#1379`, the body half of `#1126`), so the driver's own value goes
                              // out. Any other parent leaf has no row action: it is suppressed by
                              // editing it and given back by the chip's revert. Never inside an
                              // array, which is not layered per element (`ortbBodyLeafPaths`).
                              rowLayerActionKind(
                                inheritance,
                                ortbBodyLeafPaths(leaf.path),
                                ortbBodyLeafPaths(leaf.path).length > 0 &&
                                  overriddenMergeBaseField(leaf, mode) !== undefined,
                              ),
                              label,
                              {
                                remove: () => removeLeaf(label),
                                stopOverriding: stopOverriding ? () => stopOverriding(leaf) : null,
                              },
                            )}
                            editLabel={`Edit mapping for ${label}`}
                            /* The key is what the row IS — it truncates, but it never
                               collapses. `MappingRow` reserves the same band for it that
                               the list editors do, which is the alignment #989 is about:
                               the tree does not indent its rows (nesting shows in the
                               dotted label), so a left-packed pill lands at one x on every
                               row of every section. */
                            name={displayRel}
                            nameTitle={
                              wireName ? `${rel} — sent as ${wireName} on OpenRTB ${ortbVersion ?? '2.6'}` : rel
                            }
                            nameInput={
                              /* **Renamed in the open row, as every mapping row's name is** (Todd,
                                 2026-09-28; `MappingRow` draws the box only while the row is open). Not
                                 on a row the parent has: its path is the parent's too, and moving it away
                                 would leave the parent's field in force beneath — ✕ removes the parent's,
                                 and Add field adds this source's own. */
                              rowInheritanceProvenance(inheritance, ortbBodyLeafPaths(leaf.path)) === undefined ? (
                                <LeafNameInput
                                  key={label}
                                  name={leafName(leaf)}
                                  label={rel}
                                  onRename={(next) => renameOpenLeaf(leaf, next)}
                                  // The catalog's names for this section, as *Add field* offers them, less
                                  // the ones already in the body, which a rename could not take.
                                  suggest={(q) =>
                                    getFieldSuggestions(
                                      group.section,
                                      q,
                                      sectionPresentRelPaths(group.section),
                                      ortbVersion ?? undefined,
                                    ).map((f) => ({
                                      value: f.name,
                                      category: f.importance,
                                      description: f.description,
                                    }))
                                  }
                                />
                              ) : null
                            }
                            marks={{
                              // See `RequestParameterEditor` — the leaf's own findings, at the leaf.
                              severity: rowSeverity(addressedIssues, label),
                              secret: leaf.secret === true,
                              ...originMarks(
                                leafInheritanceMark(leaf),
                                readOnly ? null : () => revertLeaf(label),
                                templateRevertFor(leaf, label),
                              ),
                              // The default standing behind the mapping (#913/#1128) — the third
                              // surface that authors one, and the one that was still not showing it.
                              default: leafDefault(leaf, expressionNameFor),
                              sharedChanged: leafAddressesChangedExpression(leaf, expressionNameFor),
                              ...rowOccurrenceMarks(occurrences, leaf, { surface: 'ortbBody', name: label }),
                              // Resolution continues the value inside the pill (R4d) rather
                              // than sitting beside it, as it always has on the list rows.
                              // Suppressed on a static leaf for the reason it always was:
                              // a literal's resolution is itself.
                              ...(preview && leaf.kind !== 'static'
                                ? { resolution: resolutionForLeaf(leaf.path, preview) ?? undefined }
                                : {}),
                              /* **The exception the sentence above names** (#1112): a concept
                                 leaf is a literal whose resolution is *not* itself — the
                                 operator writes `instream` and the wire carries `plcmt: 1`.
                                 Declared from the catalog rather than observed from a preview,
                                 so it is true before anything is applied and is never stale. */
                              ...(conceptResolution ? { resolution: conceptResolution } : {}),
                              /* **The claim the row could not state** (`N11g`, extended #1112's
                                 afternoon). A leaf has carried `optional` since `#1184` and the
                                 collapsed row said nothing — so the flag was visible only to
                                 whoever expanded the row that already shows the checkbox. Same
                                 `state` slot the parameter row uses, so *never send* and *always
                                 expected* remain mutually exclusive by construction. */
                              state: (
                                <RowStateMark
                                  suppressed={!!leaf.suppress}
                                  required={describeLeafRequiredMark(leaf, isTemplate)}
                                />
                              ),
                            }}
                          >
                            <MappingControl
                              leaf={leaf}
                              declaredType={catalogValueKindForPath(leaf.path) ?? null}
                              onSaveToLibrary={onSaveExpressionToLibrary}
                              /* The outbound context schema, and the generic example — a body path
                                 reads from the same session every other outbound surface does, marked
                                 where the driver's session does not carry a path (`#1524`). */
                              pathSuggestions={(q) => getValueSuggestionsForSurface(q, 'ortbBodyTemplate')}
                              pathPlaceholder={null}
                              /* The concept vocabulary, filtered to what this source's version
                                 can express (`N11h`, #1112) — so the operator picks a meaning
                                 (`accompanying`) rather than an integer from an IAB list, and is
                                 never offered a member the driver would then omit. */
                              staticSuggestions={catalogConceptMembersForPath(leaf.path, ortbVersion ?? undefined)}
                              onChange={(next) => updateLeaf(label, next)}
                              can={{
                                // The driver reads an empty result as unresolved, so its own value at this path stands.
                                emptyValue: 'left-off',
                                /* A body leaf resolves from the same session, and since `#1184` a
                                   macro leaf is a node that can carry a type — so the macro is a
                                   first-class value here (`X27`). */
                                acceptsMacro: true,
                                acceptsDerived: true,
                                /* Every value on this surface reads the session, which is what a library expression
                                   reads too (`X28a`). */
                                offersLibrary: true,
                                /* An `$expr` body leaf is resolved against `openrtb.driver.ts`'s own
                                   context — outbound, so no `response`. */
                                resolvesAgainst: 'ortbBodyTemplate',
                                /* A JSON body carries a structure, which is the whole reason
                                   `WireSurfaceClass` has more than one member. */
                                surface: 'structured',
                                /* The body stores `$valueType` on the expression node, so this is
                                   the surface the scalar-type select was built for. */
                                storesValueType: true,
                                /* **Offered where a list could reach a field that takes one value**
                                   (`4f`, `#1416`). It was declined on the premise that *a JSON body
                                   can carry every shape* — true of the surface and false of the slot:
                                   `site.page` is a JSON leaf that takes a string. `bodyLeafRendering`
                                   is the rule, and the shape finding reads the same one. */
                                sendAs: (() => {
                                  const rendering = bodyLeafRendering(
                                    leaf,
                                    (ref) => expressionNameFor(ref)?.expression,
                                  );
                                  return rendering.offered
                                    ? {
                                        value: leaf.sendAs,
                                        onChange: (sendAs: WireRendering | undefined) =>
                                          updateLeaf(label, { ...leaf, sendAs }),
                                        sends: rendering.sends,
                                      }
                                    : null;
                                })(),
                                /* **`X33a`, and ticked by default because that is what the driver
                                   does** (Todd, 2026-09-08: *"for ORTB body, let's keep the checkbox
                                   ticked as the default to match current behavior"*). A leaf that
                                   resolves to nothing returns `UNRESOLVED`, deliberately distinct
                                   from `SUPPRESSED` — *"falling back to what the driver derived is
                                   better than sending nothing"* (`#880`) — so the value stands.

                                   **Offered since `#1266`**: declining returns the other sentinel,
                                   which the driver already has. It is *not* `$suppress` — that
                                   strips whatever happens, this strips only when the leaf's own
                                   value did not resolve.

                                   **`from` is per surface rather than per path**, because
                                   `ORTB_MERGE_BASE` answers *which* leaves the driver builds and its
                                   `certainty` has three states; wiring that per path is its own
                                   change, and claiming a specific source here would be a `#1045`
                                   sentence we cannot support. */
                                acceptsSystemDefault: {
                                  from: 'what the driver builds at this path',
                                  value: leaf.acceptSystemDefault !== false,
                                  onChange: (next) =>
                                    updateLeaf(label, { ...leaf, acceptSystemDefault: next ? undefined : false }),
                                },
                                /* An unresolved leaf **omits the key** (#880) — a literal
                                   `{token}` is a type/enum violation an exchange may reject,
                                   possibly failing the whole request. */
                                whenUnresolved: 'omit',
                                /* **Offered, now that a macro leaf has somewhere to keep one**
                                   (`#1184`). It was refused on the grounds that *the body is sent as
                                   written* — which was never true of a template — and the real
                                   constraint was ours: a macro serialised to a bare string, and a
                                   string has nowhere to put a default. The node form fixes that, and
                                   with it `N11f` closes on the last surface that still had it. */
                                storesMacroDefault: true,
                                /* **Both, now** (`#1184`). *Never send* sits beside the mapping
                                   rather than replacing it, which was the requirement — an operator
                                   toggling it while testing keeps what they authored. It reaches the
                                   driver, because it changes the wire: the key is omitted *and* what
                                   bidBrain built at that path is stripped. `optional` stays an
                                   authoring claim in the side-car, which the driver never reads. */
                                /* **The claim `$optional` was actually carrying** (`X46`). On this
                                   surface it never decided anything on the wire — an unresolved
                                   body key is omitted either way (#880) — so it was the
                                   required-ness declaration wearing the omission word. It has its
                                   own slot now, and the omission control below refuses the answer
                                   it cannot express. */
                                declaresImportance: {
                                  value: leaf.importance,
                                  onChange: (next) => updateLeaf(label, { ...leaf, importance: next }),
                                },
                                declaresSecret: {
                                  value: leaf.secret === true,
                                  onChange: (next) => updateLeaf(label, { ...leaf, secret: next }),
                                  encryptedAtRest,
                                },
                                declaresOmission: {
                                  value: omissionOf({ optional: true, suppress: leaf.suppress }),
                                  onChange: (next) =>
                                    updateLeaf(label, {
                                      ...leaf,
                                      suppress: omissionToRow(next).suppress || undefined,
                                    }),
                                  refuses: {
                                    optional: {
                                      reason:
                                        'An ORTB body key that resolves to nothing is left off either way, so this decides nothing here.',
                                    },
                                  },
                                  /* Nothing layers under a body leaf — see `RequestParameterEditor`. */
                                  layering: null,
                                },
                              }}
                            />
                          </MappingRow>
                        </Box>
                      );
                    })}

                    {!readOnly &&
                      (addingTo === group.section ? (
                        <VStack align="stretch" gap={0} py={1}>
                          <HStack px={2} gap={2}>
                            {ORTB_PATTERN_SECTIONS.has(group.section) && (
                              <PatternScopeSelect value={addScope} onChange={setAddScope} />
                            )}
                            <Input
                              size="xs"
                              bg="bg.card"
                              fontFamily="mono"
                              autoFocus
                              value={newKey}
                              placeholder="field name — standard fields autocomplete, or a custom nested.path"
                              onChange={(e) => setNewKey(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && addField(group.section)}
                            />
                            <Button
                              size="xs"
                              bg="accent.solid"
                              color="fg.onAccent"
                              _hover={{ bg: 'accent.ink' }}
                              disabled={!addScope}
                              onClick={() => addField(group.section)}
                            >
                              Add
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => {
                                setAddingTo(null);
                                setNewKey('');
                              }}
                            >
                              Cancel
                            </Button>
                          </HStack>
                          {!addScope && (
                            <Text px={2} pt={1} fontSize="xs" color="warning.ink">
                              Enter the impression&apos;s number, counting from 1, to add a field for it.
                            </Text>
                          )}
                          {(fieldSuggestions.length > 0 || moveOffers.length > 0) && (
                            <VStack
                              align="stretch"
                              gap={0}
                              mx={2}
                              mt={1}
                              borderWidth="1px"
                              borderColor="border.default"
                              borderRadius="md"
                              bg="bg.card"
                              overflow="hidden"
                            >
                              {moveOffers.map(({ field, from }) => (
                                <HStack
                                  key={`move-${field.name}`}
                                  as="button"
                                  px={3}
                                  py={1.5}
                                  gap={2}
                                  textAlign="left"
                                  _hover={{ bg: 'accent.subtle' }}
                                  aria-label={`Move ${from} to ${field.name}`}
                                  onClick={() => {
                                    addFixLeaves([{ section: group.section, relPath: field.name, movedFrom: from }]);
                                    setAddingTo(null);
                                    setNewKey('');
                                  }}
                                >
                                  <Text fontFamily="mono" fontSize="xs" color="fg.default" flexShrink={0}>
                                    {field.name}
                                  </Text>
                                  <Text fontSize="xs" color="accent.ink" flex="1" truncate>
                                    Move{' '}
                                    <Text as="span" fontFamily="mono">
                                      {from}
                                    </Text>{' '}
                                    here — OpenRTB {ortbVersion ?? '2.6'} reads this location
                                  </Text>
                                </HStack>
                              ))}
                              {fieldSuggestions.map((f) => (
                                <HStack
                                  key={f.name}
                                  as="button"
                                  px={3}
                                  py={1.5}
                                  gap={2}
                                  textAlign="left"
                                  _hover={{ bg: 'accent.subtle' }}
                                  // Refused while a numbered impression has no valid number — said, not silent.
                                  aria-disabled={!addScope}
                                  opacity={addScope ? 1 : 0.5}
                                  cursor={addScope ? 'pointer' : 'not-allowed'}
                                  onClick={() => addScope && addFieldByPath(group.section, f.name, true, addScope)}
                                >
                                  <Text fontFamily="mono" fontSize="xs" color="fg.default" flexShrink={0}>
                                    {f.name}
                                  </Text>
                                  <Text fontFamily="mono" fontSize="xs" color="fg.muted" flexShrink={0}>
                                    {f.valueType}
                                  </Text>
                                  <Text fontSize="xs" color="fg.muted" flex="1" truncate>
                                    {f.description}
                                  </Text>
                                  <Badge
                                    flexShrink={0}
                                    fontFamily="mono"
                                    fontSize="xs"
                                    borderRadius="full"
                                    px={2}
                                    bg={
                                      f.importance === 'required'
                                        ? 'error.tint'
                                        : f.importance === 'recommended'
                                          ? 'info.tint'
                                          : 'bg.subtle'
                                    }
                                    color={
                                      f.importance === 'required'
                                        ? 'error.ink'
                                        : f.importance === 'recommended'
                                          ? 'info.ink'
                                          : 'fg.muted'
                                    }
                                  >
                                    {IMPORTANCE_LABEL[f.importance]}
                                  </Badge>
                                </HStack>
                              ))}
                            </VStack>
                          )}
                        </VStack>
                      ) : (
                        <HStack gap={1} alignSelf="start">
                          <Button
                            size="xs"
                            variant="ghost"
                            color="accent.solid"
                            onClick={() => {
                              setAddingTo(group.section);
                              setNewKey('');
                              setAddScope({ kind: 'each' });
                            }}
                          >
                            ＋ Add {group.section} field
                          </Button>
                          {/* Reads as an action, not a caption. Ghost + `fg.muted` put
                              it in the same grey as the section's own labels, so it was
                              read as a count and never clicked — operators went to
                              "+ Add … field" and typed the names by hand instead. It
                              sits beside an accent-coloured sibling, which is exactly
                              the contrast that taught them it was not clickable. */}
                          {missingStd.length > 0 && (
                            <Button
                              size="xs"
                              variant="ghost"
                              color="accent.solid"
                              fontWeight="600"
                              _hover={{ bg: 'accent.subtle' }}
                              onClick={() => addRecommended(group.section)}
                            >
                              + Add {missingStd.length} recommended
                            </Button>
                          )}
                        </HStack>
                      ))}
                  </VStack>
                )}
              </Box>
            );
          })}

          {/* Add a whole OpenRTB section (or a custom one) */}
          {!readOnly && (
            <Box px={4} py={3} mt={1} borderTopWidth="1px" borderColor="border.subtle">
              <HStack gap={2} flexWrap="wrap" align="center">
                <Text fontSize="xs" fontFamily="mono" color="fg.muted">
                  Add section
                </Text>
                {STANDARD_SECTIONS.filter((s) => !presentSections.has(s)).map((s) => (
                  <Button
                    key={s}
                    size="xs"
                    variant="outline"
                    borderColor="border.default"
                    color="accent.solid"
                    fontFamily="mono"
                    onClick={() => addSection(s)}
                  >
                    {s}
                  </Button>
                ))}
                {addingCustomSection ? (
                  <HStack gap={1}>
                    <Input
                      size="xs"
                      bg="bg.card"
                      fontFamily="mono"
                      autoFocus
                      value={customSection}
                      placeholder="custom"
                      onChange={(e) => setCustomSection(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && commitCustomSection()}
                    />
                    <Button
                      size="xs"
                      bg="accent.solid"
                      color="fg.onAccent"
                      _hover={{ bg: 'accent.ink' }}
                      onClick={commitCustomSection}
                    >
                      Add
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        setAddingCustomSection(false);
                        setCustomSection('');
                      }}
                    >
                      Cancel
                    </Button>
                  </HStack>
                ) : (
                  <Button size="xs" variant="ghost" color="fg.muted" onClick={() => setAddingCustomSection(true)}>
                    ＋ custom
                  </Button>
                )}
              </HStack>
            </Box>
          )}
        </Box>
      )}

      {confirmLeaveRaw && (
        <ConfirmDialog
          title="Switch to Guided and lose what doesn't parse?"
          body="The JSON has an error, so Guided can only show the body as it was when it last parsed. Anything you typed since then will be lost. Stay to fix the error first."
          confirmLabel="Switch and discard"
          cancelLabel="Stay in Raw JSON"
          onConfirm={() => {
            setConfirmLeaveRaw(false);
            setRawError(null);
            setRawQuoteFix(null);
            setView('guided');
          }}
          onCancel={() => setConfirmLeaveRaw(false)}
        />
      )}

      {/* raw view — editable CodeMirror with schema completion + JSON lint */}
      {effectiveView === 'raw' && (
        <Box px={4} py={4}>
          <Box
            borderWidth="1px"
            borderColor={rawError ? 'error.border' : 'border.default'}
            borderRadius="md"
            overflow="hidden"
          >
            <React.Suspense fallback={<LoadingState py={6} label="Loading the JSON editor…" />}>
              <OrtbRawJsonEditor
                key={rawSeed}
                ortbVersion={ortbVersion}
                initialText={JSON.stringify(leavesToBody(leaves), null, 2)}
                onValidBody={(body) => {
                  setRawQuoteFix(null);
                  const next = bodyToLeaves(body);
                  emit(next, mode);
                  // Did a *sample* just land here rather than in Paste & parse (#922 · 5)?
                  // The edit is applied either way — this only offers the alternative
                  // reading. Dismissing is remembered for the session, so an operator who
                  // meant the literals is not asked again on their next keystroke.
                  if (!sampleOfferDismissed) setPastedSample(diagnosePastedSample(body));
                }}
                onError={(message, fix) => {
                  setRawError(message);
                  setRawQuoteFix(fix?.normalized ?? null);
                }}
              />
            </React.Suspense>
          </Box>
          {/* The wrong-window paste (#922 · 5). Stated, never guessed: the edit is
              already applied, and this offers the other reading of it. A literal is a
              legitimate choice — `tagid`, `at`, a publisher id — so silently mapping the
              document would be the mirror-image of the defect being prevented. */}
          {pastedSample && (
            <Box mt={2} p={3} bg="warning.tint" borderWidth="1px" borderColor="warning.border" borderRadius="6px">
              <Text fontSize="xs" fontWeight="600" color="warning.ink" mb={1}>
                That looks like a sample bid request, not a template.
              </Text>
              <Text fontSize="xs" color="fg.muted" mb={2}>
                Raw JSON <strong>is</strong> the template, so {pastedSample.pinned.length} captured value
                {pastedSample.pinned.length === 1 ? '' : 's'} would be sent to every viewer exactly as written — one
                person&apos;s{' '}
                <Text as="span" fontFamily="mono">
                  {pastedSample.pinned.slice(0, 3).join(', ')}
                </Text>
                {pastedSample.pinned.length > 3 ? ` and ${pastedSample.pinned.length - 3} more` : ''}. Mapping them
                instead sends each viewer&apos;s own.
              </Text>
              <HStack gap={2}>
                <Button
                  size="xs"
                  bg="accent.solid"
                  color="fg.onAccent"
                  _hover={{ bg: 'accent.ink' }}
                  onClick={() => {
                    // The same mapping the Paste & parse path applies — one importer, so
                    // the two windows cannot come to disagree about what a sample means.
                    emit(
                      leaves.map((l) => {
                        const token = pastedSample.proposed[pathToLabel(l.path)];
                        return token
                          ? ({ ...l, kind: 'expression', expression: macroExpression(token) } as MappingLeaf)
                          : l;
                      }),
                      mode,
                    );
                    setRawSeed((n) => n + 1);
                    setPastedSample(null);
                  }}
                >
                  Map {pastedSample.pinned.length} value{pastedSample.pinned.length === 1 ? '' : 's'}
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  color="fg.muted"
                  onClick={() => {
                    setPastedSample(null);
                    setSampleOfferDismissed(true);
                  }}
                >
                  Keep as literal values
                </Button>
              </HStack>
            </Box>
          )}
          {rawError ? (
            <HStack mt={2} gap={2} align="flex-start">
              <Text fontSize="xs" color="error.ink">
                {rawError}
              </Text>
              {rawQuoteFix && (
                <Button
                  size="xs"
                  variant="outline"
                  flexShrink={0}
                  onClick={() => {
                    try {
                      const body = JSON.parse(rawQuoteFix) as Record<string, unknown>;
                      emit(bodyToLeaves(body), mode);
                      setRawError(null);
                      setRawQuoteFix(null);
                      // Re-seed so the corrected text is what the operator sees — the
                      // editor is uncontrolled, so without this the body updates while
                      // the visible text keeps its curly quotes.
                      setRawSeed((n) => n + 1);
                    } catch {
                      /* diagnose only returns a fix that parses; nothing to do */
                    }
                  }}
                >
                  Fix quotes &amp; parse
                </Button>
              )}
            </HStack>
          ) : (
            <Text fontSize="xs" color="fg.muted" mt={2}>
              Type{' '}
              <Text as="span" fontFamily="mono">
                {'{'}
              </Text>{' '}
              for macros, a key for OpenRTB fields, or{' '}
              <Text as="span" fontFamily="mono">
                $expr
              </Text>{' '}
              for an expression. Complex logic is easier in Guided.
            </Text>
          )}
        </Box>
      )}
      {applyToAll.dialog}
    </Box>
  );
};

/**
 * **Rows scoped to every impression first, then by index, then the last** (RS4c) — the order the
 * driver layers them in, so the row read last is the one that wins. Stable within a scope, so the
 * order fields were added in is kept.
 */
function orderedByScope(leaves: MappingLeaf[]): MappingLeaf[] {
  return leaves
    .map((leaf, index) => ({ leaf, index, rank: patternScopeRank(patternScopeOf(leaf.path)) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ leaf }) => leaf);
}

/**
 * **Which impressions a field being added is for** — see `addScope`. *Every impression*, *the last
 * impression*, or a numbered one, counted from one as a person counts; the stored key is the wire's
 * zero-based index. There is no upper bound: the pod decides how many impressions it asks for
 * (`#1099` removed the old cap of ten), and an override past what a round builds is simply not
 * applied (RS3).
 */
const PatternScopeSelect: React.FC<{ value: PatternScope | null; onChange: (next: PatternScope | null) => void }> = ({
  value,
  onChange,
}) => {
  const mode = value === null ? 'index' : value.kind === 'each' ? 'each' : value.key === 'last' ? 'last' : 'index';
  const [number, setNumber] = React.useState<number | undefined>(
    value && value.kind === 'at' && value.key !== 'last' ? Number(value.key) + 1 : 1,
  );
  const scopeFor = (n: number | undefined): PatternScope | null =>
    n !== undefined && Number.isInteger(n) && n >= 1 ? { kind: 'at', key: String(n - 1) } : null;
  return (
    <HStack gap={1} flexShrink={0}>
      <NativeSelect.Root size="xs" width="auto">
        <NativeSelect.Field
          aria-label="Which impressions this field is for"
          bg="bg.card"
          value={mode}
          onChange={(e) => {
            const next = e.target.value;
            if (next === 'each') onChange({ kind: 'each' });
            else if (next === 'last') onChange({ kind: 'at', key: 'last' });
            else onChange(scopeFor(number));
          }}
        >
          <option value="each">for every impression</option>
          <option value="index">for impression number…</option>
          <option value="last">for the last impression</option>
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
      {mode === 'index' && (
        <NumberField
          minDecimals={null}
          size="xs"
          width="4.5rem"
          bg="bg.card"
          aria-label="Impression number, counting from 1"
          placeholder="1"
          value={number}
          onChange={(n) => {
            setNumber(n);
            onChange(scopeFor(n));
          }}
        />
      )}
    </HStack>
  );
};

/**
 * **The open row's name, as an input** (Todd, 2026-09-28). A draft the operator types into, committed
 * on Enter or on leaving the box — not per keystroke, because the row is keyed by its path and a rename
 * on every key would move it under the operator's cursor. A refused name stays in the box with the
 * reason beside it, marked invalid, and the field keeps its old name until it is fixed.
 */
const LeafNameInput: React.FC<{
  /** The editable part of the path — see `leafName`. */
  name: string;
  /** The row's full section-relative label, for the accessible name. */
  label: string;
  onRename: (next: string) => string | null;
  /** What a rename may be completed to — the section's catalog fields not already in the body. */
  suggest: (query: string) => MappingSuggestion[];
}> = ({ name, label, onRename, suggest }) => {
  const [draft, setDraft] = React.useState(name);
  const [problem, setProblem] = React.useState<string | null>(null);
  const commit = () => setProblem(onRename(draft));
  // **The part a rename keeps stays on screen** (`[*].`, `[last].`): which impressions a field is for
  // is half of what the row is, and an input holding only `tagid` would drop it the moment it opened.
  const kept = label.endsWith(name) ? label.slice(0, label.length - name.length) : '';
  return (
    <VStack align="stretch" gap={0.5} w="100%">
      <HStack gap={0.5}>
        {kept && (
          <Text fontFamily="mono" fontSize="sm" color="fg.muted" flexShrink={0} data-testid="leaf-name-kept">
            {kept}
          </Text>
        )}
        <Box flex="1" minW={0}>
          <MappingAutocompleteInput
            size="sm"
            fontFamily="mono"
            bg="bg.subtle"
            minChars={0}
            value={draft}
            aria-label={`Field name for ${label}`}
            aria-invalid={problem ? true : undefined}
            getSuggestions={suggest}
            onChange={(next) => {
              setDraft(next);
              setProblem(null);
            }}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                commit();
              }
            }}
          />
        </Box>
      </HStack>
      {problem && (
        <Text fontSize="xs" color="error.ink" role="alert">
          {problem}
        </Text>
      )}
    </VStack>
  );
};

/** The worst finding's dot and the count, on a body group's header — see its call site. */
const GroupFindingsMark: React.FC<{ issues: readonly MappingIssue[] }> = ({ issues }) => {
  const worst = worstIssue(issues);
  if (!worst) return null;
  return (
    <HStack gap={1} flexShrink={0} data-group-findings="">
      <IssueDot level={issueLevel(worst)} title={worst.message} />
      {issues.length > 1 && (
        <Text as="span" fontSize="xs" color="fg.muted">
          {issues.length}
        </Text>
      )}
    </HStack>
  );
};
